/**
 * Learning (Mastery) mode page.
 * Routes:
 *   /decks/:deckId/learn
 *   /folders/:folderId/learn
 *   /courses/:courseId/learn
 *
 * Keyboard: Enter = submit/retype, Escape = close overlay
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSpeech } from '../hooks/useSpeech'
import { useAuthStore } from '../stores/authStore'
import { useLearningSession } from '../hooks/useLearningSession'
import { buildFillInBlank } from '../utils/learningUtils'
import { findTermSide, isTermSide } from '../utils/cardSides'
import { learnDefinitionSide, learnExampleSide } from '../utils/learnCardFields'
import { isAllowedSide, isImageSide, definitionModeSupplementarySides } from '../utils/learnSupplementarySides'
import { useLocale } from '../i18n/context'
import {
  useLadderFontSize,
  PROMPT_FONT_LADDER,
  PROMPT_FONT_LADDER_BASE_IDX,
} from '../hooks/useLadderFontSize'
import { learnPromptLabel } from '../utils/learnPromptLabel'

// ── Learn-card mobile layout tokens (see DESIGN_SYSTEM.md, ADR-012, ADR-013) ─
// The card has no hardcoded min/max height on mobile — it fills the 1fr row
// of the page grid, so its size is derived from
//   viewport - header - DefinitionTrigger - input - keyboard - gaps.
// Prompt content uses the font ladder (hooks/useLadderFontSize); internal
// scrolling activates only when content still doesn't fit at the floor size.
const LEARN_CARD_BUTTON_GAP = 4

// ── Retype reveal two-phase timing (ADR-014 amendment, 2026-07-14) ───────────
// On a wrong typed answer the correct word appears in the input overlay at full
// red strength, HOLDS for a readable pause, then fades to a neutral (non-red)
// ghost that reads as a placeholder inviting the user to retype. The red phase
// is the in-the-moment error signal; the ghost is the standing invitation.
// Values align with the design system ghost-text token (opacity 0.4, 300ms
// ease-in — DESIGN_SYSTEM.md "Ghost text (retype)").
const RETYPE_READ_PAUSE_MS = 700          // red hold before the fade starts (readable ~0.7s)
const RETYPE_GHOST_FADE_MS = 300          // red→ghost fade duration (design token)
const RETYPE_GHOST_OPACITY = 0.4          // ghost final opacity (design ghost token)
// localStorage key that records the retype coach mark has been shown to this user.
// Written on first dismiss (tap or type-to-continue); never cleared, so the mark
// is shown at most once per device across all sessions.
const RETYPE_COACH_KEY = 'cs_retype_coach_shown'

import DefinitionBottomSheet from '../components/learning/DefinitionBottomSheet'
import GrammarNoteCard from '../components/learning/GrammarNoteCard'
import MasteryBadge from '../components/learning/MasteryBadge'
import DeckStatusBadge from '../components/shared/DeckStatusBadge'
import MultipleChoiceExercise from '../components/learning/MultipleChoiceExercise'
import ListeningExercise from '../components/learning/ListeningExercise'
import SentenceBuildExercise from '../components/learning/SentenceBuildExercise'
import BracketCorners from '../components/learning/BracketCorners'
import RetypeCoachMark from '../components/learning/RetypeCoachMark'
import ClickableText from '../components/learning/ClickableText'
import FillInBlankSentence from '../components/learning/FillInBlankSentence'
import LearningComplete from '../components/learning/LearningComplete'
import ProgressBar from '../components/learning/ProgressBar'
import PreviousCardOverlay from '../components/learning/PreviousCardOverlay'
import DayBoundaryOverlay from '../components/learning/DayBoundaryOverlay'
import ShowAllOverlay from '../components/learning/ShowAllOverlay'
import SideTabs from '../components/learning/SideTabs'
import WordReferencePanel from '../components/learning/WordReferencePanel'
import WordReferenceTrigger from '../components/learning/WordReferenceTrigger'
import { DefinitionTranslationToggle } from '../components/learning/DefinitionTranslation'

/**
 * Does this box overflow the content it is rendering RIGHT NOW, and is there
 * more of it above or below what is in view? (ADR-100 D7.)
 *
 * `useLadderFontSize`'s `shouldScroll` is the ladder's verdict on the text it
 * walked. Between walks, the caption's "More" can take height away from the
 * prompt zone through flex and overflow a box the ladder declared safe. Measured
 * on every render, on resize, and on scroll; state written only on change,
 * which converges: engaging the affordance adds padding, and padding never
 * shrinks scrollHeight.
 */
type RenderedOverflow = { overflowing: boolean; moreAbove: boolean; moreBelow: boolean }
function useRenderedOverflow(ref: RefObject<HTMLElement | null>, attachKey: unknown): RenderedOverflow {
  const [state, setState] = useState<RenderedOverflow>({ overflowing: false, moreAbove: false, moreBelow: false })
  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const overflowing = el.scrollHeight > el.clientHeight + 1
    const moreAbove = overflowing && el.scrollTop > 1
    const moreBelow = overflowing && el.scrollTop + el.clientHeight < el.scrollHeight - 1
    setState((prev) => (
      prev.overflowing === overflowing && prev.moreAbove === moreAbove && prev.moreBelow === moreBelow
        ? prev
        : { overflowing, moreAbove, moreBelow }
    ))
  }, [ref])
  // Every render of this page, for the swap (the text changes under a box of
  // the same size)…
  useLayoutEffect(measure)
  // …a ResizeObserver for the changes this page never re-renders for (the
  // caption's "More" is local state inside DefinitionCaption: expanding it
  // takes height from the prompt zone through flex alone), observed on the box
  // and on its first child; and scroll, which moves what is in view.
  // attachKey (the card, plus the swap) re-runs this: the box does not exist on
  // the page's first render, and an effect that only ran then would observe
  // nothing for the rest of the session.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    if (el.firstElementChild) ro.observe(el.firstElementChild)
    el.addEventListener('scroll', measure, { passive: true })
    return () => {
      ro.disconnect()
      el.removeEventListener('scroll', measure)
    }
  }, [ref, measure, attachKey])
  return state
}

// The scroll affordance's fade (ADR-027 H2), deepened on the owner's
// instruction of 2026-09-21: "the region scrolls, with the fade showing there
// is more". An 8 px fade sat in the gap between two lines and faded nothing, so
// a box cut mid-sentence read as a sentence that ended. An edge with more text
// beyond it now fades over about one line, so the line there visibly dims; an
// edge with nothing beyond keeps the 8 px guard, so the first line is crisp at
// the top and the last one is crisp once scrolled to the end.
const SCROLL_FADE_GUARD_PX = 8
const SCROLL_FADE_MORE_PX = 28
function scrollFadeMask({ moreAbove, moreBelow }: RenderedOverflow) {
  const top = moreAbove ? SCROLL_FADE_MORE_PX : SCROLL_FADE_GUARD_PX
  const bottom = moreBelow ? SCROLL_FADE_MORE_PX : SCROLL_FADE_GUARD_PX
  const mask = `linear-gradient(to bottom,transparent 0,#000 ${top}px,#000 calc(100% - ${bottom}px),transparent 100%)`
  return { maskImage: mask, WebkitMaskImage: mask }
}

/**
 * Supporting definition line for fill-in-the-blank Learn cards. Rendered as a
 * quiet caption (matching the weight of the form/translation lines) rather than
 * body text, so the reclaimed vertical space goes to the example sentence.
 *
 * On mobile Learn decides its size and whether it clamps (`fit`, see
 * CAPTION_FIT_STEPS): shown whole where the card has room beside the sentence,
 * and clamped to two lines with an unobtrusive "More" only when it cannot be.
 * Per the mastery ADR the definition is always visible — it is never hidden
 * below two lines' worth of content pre-expansion. Desktop shows the full text
 * (no clamp).
 *
 * Expanding is user-initiated and MUST NOT drop the soft keyboard: the toggle
 * prevents default on pointer/mouse-down so it never steals focus from the
 * answer input (ADR-011/013 — keyboard stays raised mid-card).
 */
// The caption's size, and how it is fitted beside the sentence (owner's rule,
// 2026-09-21, for the English caption and its translation alike): the sentence
// and the WHOLE caption fit together before either is cut. In order: whole at
// 13, 12, 11 px, the sentence re-fitted by the ladder after each step; then,
// only if the sentence still cannot fit at the ladder's floor, clamped at 11
// with "More" — and the sentence keeps its full text (scrolling, with the fade,
// as the last resort).
const CAPTION_FONT_PX = 13
const CAPTION_FIT_STEPS = [
  { fontSize: 13, clamp: false },
  { fontSize: 12, clamp: false },
  { fontSize: 11, clamp: false },
  { fontSize: 11, clamp: true },
] as const

function DefinitionCaption({
  text,
  isMobile,
  speak,
  isSpeechSupported,
  resetKey,
  translatedLanguage,
  fit,
}: {
  text: string
  isMobile: boolean
  speak: (word: string) => void
  isSpeechSupported: boolean
  resetKey: unknown
  /** Set while `text` is the Definition's translation (ADR-100 D7): the
   *  reader's language code, carried as the `lang`. */
  translatedLanguage?: string | null
  /** The size Learn chose, and whether it clamps. Without it: 13 px, clamped
   *  on mobile. */
  fit?: { fontSize: number; clamp: boolean }
}) {
  const { tr } = useLocale()
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const ref = useRef<HTMLParagraphElement | null>(null)
  const fontSize = fit?.fontSize ?? CAPTION_FONT_PX

  // New card → collapse again.
  useLayoutEffect(() => { setExpanded(false) }, [resetKey])

  // Detect truncation only while clamped (collapsed). When expanded the clamp
  // is removed so scrollHeight==clientHeight; skipping the measure there keeps
  // the last collapsed value so the "Less" affordance stays visible.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !isMobile || expanded) return
    setOverflowing(el.scrollHeight > el.clientHeight + 1)
  }, [text, resetKey, isMobile, expanded, fontSize, fit?.clamp])

  // Clamped only when Learn found no way to show it whole beside the sentence.
  const clamped = isMobile && !expanded && (fit ? fit.clamp : true)

  return (
    <div style={{ margin: isMobile ? '0 0 4px' : '0 0 12px', width: '100%' }}>
      <p
        ref={ref}
        data-testid="definition-caption"
        lang={translatedLanguage ?? undefined}
        // Prevent focus theft so the answer input keeps the keyboard raised
        // (ADR-013). onClick on child ClickableText spans still fires (comes
        // from pointerup, not the cancelled compat mousedown).
        onPointerDown={(e) => e.preventDefault()}
        style={{
          margin: 0,
          textAlign: 'center',
          fontSize,
          fontFamily: 'var(--font-content)',
          color: 'var(--text-secondary)',
          lineHeight: 1.45,
          paddingInline: 8,
          ...(clamped ? {
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : {}),
        }}
      >
        {translatedLanguage
          ? text
          : <ClickableText text={text} speak={speak} isSpeechSupported={isSpeechSupported} />}
      </p>
      {isMobile && overflowing && (
        <button
          type="button"
          aria-expanded={expanded}
          // Prevent focus theft so the answer input keeps the soft keyboard
          // raised (ADR-011/013 — the keyboard must never retract mid-card).
          onPointerDown={(e) => e.preventDefault()}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: 'block',
            margin: '1px auto 0',
            padding: '2px 10px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
            opacity: 0.75,
          }}
        >
          {expanded ? tr.learn.less : tr.learn.more}
        </button>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LearnPage() {
  const { deckId, folderId, courseId } = useParams<{
    deckId?: string
    folderId?: string
    courseId?: string
  }>()
  const navigate = useNavigate()
  const location = useLocation()
  // Re-hydrate from sessionStorage when landing on /session without router state
  // (page reload or cold-start mid-session — audit R2). Source-bound routes
  // (/decks/:id/learn etc.) always have a sourceId so they never need this path.
  const sqState = (() => {
    const fromRouter = location.state as { sessionId?: string; totalCards?: number; resolutionCards?: number; reviewedCards?: number; reviewedCorrect?: number; reviewedWrong?: number; from?: string; is_prep?: boolean; sessionTitle?: string; dayBoundaryClosed?: boolean } | null
    if (fromRouter?.sessionId) return fromRouter
    if (!deckId && !folderId && !courseId) {
      const saved = sessionStorage.getItem('cs_active_session')
      if (saved) return { sessionId: saved }
    }
    return fromRouter
  })()

  const { user } = useAuthStore()
  const { tr } = useLocale()

  // Kept for PreviousCardOverlay — controls translation display in the prev-card peek.
  const [showTranslations] = useState<boolean>(false)

  const [referenceOpen, setReferenceOpen] = useState(false)
  // When the user opens the reference / definition panels from the Previous-card
  // overlay, the panels need to target that card's data instead of the active
  // card. These overrides hold the previous card's term / sentence / definition
  // for as long as the panel is open. Cleared when the panel closes.
  const [prevReferenceTerm, setPrevReferenceTerm] = useState<string | null>(null)
  const [prevReferenceSentence, setPrevReferenceSentence] = useState<string | undefined>(undefined)
  const [prevDefinitionContent, setPrevDefinitionContent] = useState<string | null>(null)
  const [prevDefinitionExample, setPrevDefinitionExample] = useState<string | undefined>(undefined)

  // Visual viewport height — updates when the soft keyboard opens/closes on mobile.
  // Used to constrain the outer container so pinned input stays above the keyboard,
  // and as a reset signal for the auto-shrink font hook so long prompt content
  // re-fits against the post-keyboard available height (not the pre-keyboard one).
  //
  // Resize alone is sufficient on Android (Samsung Keyboard / Gboard fire it on
  // IME show/hide). The scroll subscription was previously included but produced
  // noisy intermediate values during keyboard animations on Android and offered
  // no benefit beyond what resize already delivers, so it has been removed.
  const [viewportH, setViewportH] = useState(() =>
    typeof window !== 'undefined'
      ? (window.visualViewport?.height ?? window.innerHeight)
      : 0,
  )
  useEffect(() => {
    const vv = window.visualViewport
    const update = () => setViewportH(vv ? vv.height : window.innerHeight)
    update()
    if (!vv) return
    vv.addEventListener('resize', update)
    return () => { vv.removeEventListener('resize', update) }
  }, [])

  // Compact mobile layout (<768px): no Submit button, Enter/Go key submits, ↵ icon in input
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches)
  // Tracks input focus so the static synthetic caret in the typing overlay
  // only renders while the real input has focus (see ADR-014, Prompt K and
  // the caret-model amendment).
  const [isInputFocused, setIsInputFocused] = useState(false)
  // Tap-to-reposition caret (ADR-014 caret amendment + 2026-07-15 correction):
  // the APP owns tap placement. The native input hit-tests taps against
  // uniform-width bullet glyphs (type="password"), so its own placement drifts
  // from the proportional overlay text the user aims at. `handleInputTap` maps
  // the tap x-coordinate to a character index through the overlay glyph metrics
  // and writes the native selection, then we mirror `selectionStart` here.
  // `null` means "not yet read" and falls back to end-of-text in the render.
  // `inputScrollLeft` mirrors the native input's horizontal scroll so the caret
  // (and the tap mapping) stay aligned once a long term overflows the box.
  const [caretIndex, setCaretIndex] = useState<number | null>(null)
  const [inputScrollLeft, setInputScrollLeft] = useState(0)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Refs + auto-shrink hooks for prompt content that must fit without top-crop
  const fibContentRef = useRef<HTMLDivElement | null>(null)
  const definitionContentRef = useRef<HTMLDivElement | null>(null)

  // ADR-082 boundary overlay — measures the header row's live height so the
  // overlay can be positioned below it without competing with the grid's own
  // row auto-placement. A `gridRow`-placed overlay was tried first: an
  // explicitly-placed grid item reserves its cells during layout BEFORE the
  // auto-placed header/card/input rows are assigned theirs, so it displaced
  // the real content into brand-new implicit rows sized to their own tiny
  // content instead of the intended `auto 1fr auto` tracks (verified on
  // device — the card and input squeezed to ~34px/~76px). Plain absolute
  // positioning against the grid container (which is already a containing
  // block via its own framer-motion transform) sidesteps grid placement
  // entirely; only the header's height needs to be known.
  const [headerRowHeight, setHeaderRowHeight] = useState(0)
  const headerRowObserverRef = useRef<ResizeObserver | null>(null)
  // Callback ref, not a plain ref + effect-with-[]-deps: this header only
  // exists in the DOM once phase reaches 'active' (LearnPage shows a loading
  // skeleton before that), so a `[]`-deps effect reading `.current` at first
  // mount would find null on that first commit and never fire again — exactly
  // what happened (verified on device: headerRowHeight stuck at 0 forever,
  // silently keeping the ADR-082 overlay's render gate false with everything
  // else about it correct). A callback ref runs exactly when the node is
  // actually attached or detached, whichever render that happens to be.
  const headerRowRef = useCallback((el: HTMLDivElement | null) => {
    headerRowObserverRef.current?.disconnect()
    headerRowObserverRef.current = null
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height
      if (h !== undefined) setHeaderRowHeight(h)
    })
    ro.observe(el)
    headerRowObserverRef.current = ro
  }, [])

  // Web Speech API — speaks the term in the user's target language
  const { speak, cancelSilently, isSpeaking, isSupported: isSpeechSupported } = useSpeech(user?.target_language)

  // ── Source resolution ────────────────────────────────────────────────────────
  const sourceType: 'deck' | 'folder' | 'course' = deckId ? 'deck' : folderId ? 'folder' : 'course'
  const sourceId = deckId ?? folderId ?? courseId

  // ── Session hook ─────────────────────────────────────────────────────────────
  const {
    phase, errorMsg, nextDueAt,
    isResumed, prepStats, isPractice, totalCards, resolutionCount, stats, wordsLearnedToday, blockStats,
    currentCard, prevCard, showingPrev, setShowingPrev,
    userInput, setUserInput, lastResult, resultState, setResultState,
    activeTab, setActiveTab, showAll, setShowAll,
    isShaking,
    pendingGrammarNote, mcSelectedOption,
    dayBoundaryClosed, restartingSession, continueAfterDayBoundary, retrySession,
    mcOptions,
    inputRef,
    handleSubmit, handleMCSelect, handleRetype,
    handleListeningSubmit, handleListeningRetype, handleListeningContinue,
    handleSBSubmit, handleGrammarNoteContinue,
    handleManualAdvance,
    continueExhausted, handleContinueLearning, mistakes,
    submitIsPending, retypeIsPending,
    progressSummary, milestone, postSessionWordsKnown,
  } = useLearningSession({ sourceType, sourceId, sqState, speak, cancelSpeech: cancelSilently, isSpeechSupported })

  // Mobile only: lock body/html scroll so finger gestures on the learn screen don't drag
  // the page or rubber-band on iOS Safari. Released on the completion screen, where content
  // can exceed the viewport (e.g. milestone block) and must scroll naturally instead of
  // clipping. Restored on unmount so other pages scroll normally.
  useEffect(() => {
    if (!isMobile || phase === 'complete') return
    const prevBodyOverflow = document.body.style.overflow
    const prevBodyOverscroll = document.body.style.overscrollBehavior
    const prevHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.body.style.overscrollBehavior = prevBodyOverscroll
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [isMobile, phase])

  // Soft-reject placeholder tier selection (ADR-048 §7 amendment, 2026-07-15):
  // fit-based, not a viewport breakpoint. Measures the input zone's actual
  // rendered width (which on mobile already excludes the submit button, since
  // the button is a flex sibling) and picks the longest of `tr.learn.softRejectTiers`
  // whose rendered width fits — never below the last (shortest) tier.
  const inputZoneRef = useRef<HTMLDivElement>(null)
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [placeholderTierIndex, setPlaceholderTierIndex] = useState(0)
  useLayoutEffect(() => {
    if (resultState !== 'soft_reject') return
    const el = inputZoneRef.current
    if (!el) return
    const tiers = tr.learn.softRejectTiers
    const computeFit = () => {
      const zoneWidth = el.clientWidth
      if (!zoneWidth) return
      const availWidth = zoneWidth - (isMobile ? 0 : 40) // desktop padding: 16px+20px per side reserved by the overlay div
      const canvas = measureCanvasRef.current ?? (measureCanvasRef.current = document.createElement('canvas'))
      const ctx = canvas.getContext('2d')
      if (!ctx) { setPlaceholderTierIndex(tiers.length - 1); return }
      const fontFamily = getComputedStyle(document.documentElement).getPropertyValue('--font-content').trim() || 'serif'
      ctx.font = `400 ${isMobile ? 19 : 20}px ${fontFamily}`
      const fitIndex = tiers.findIndex((t) => ctx.measureText(t).width <= availWidth)
      setPlaceholderTierIndex(fitIndex === -1 ? tiers.length - 1 : fitIndex)
    }
    computeFit()
    const ro = new ResizeObserver(computeFit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [resultState, isMobile, tr])

  // Close reference panel whenever a new card is loaded
  useEffect(() => { setReferenceOpen(false) }, [currentCard?.card_id])

  // ADR-100 Decision 7: the Definition's translation is open for one showing
  // only. A layout effect, so the next card never paints with it open.
  const [definitionTranslationOpen, setDefinitionTranslationOpen] = useState(false)
  useLayoutEffect(() => { setDefinitionTranslationOpen(false) }, [currentCard?.card_id])
  // ADR-100 D7 as re-specified 2026-09-21 — the supporting lines (form line and
  // term translation) under the reveal. They step aside only when the
  // translated text does not fit with them present, and come back when the
  // reveal closes, the card changes, or the card's height changes (keyboard up
  // or down): each of those is a new question, asked again from the lines
  // present. The ladder asks it — see `freeSupportLines` below.
  const [supportLinesHidden, setSupportLinesHidden] = useState(false)
  // The card's own height on mobile, where it is sized by the grid row and not
  // by its content, so it changes only with the viewport (keyboard, URL bar).
  // Tracked so the supporting lines are re-decided at the new height.
  const [learnCardEl, setLearnCardEl] = useState<HTMLDivElement | null>(null)
  const [learnCardHeight, setLearnCardHeight] = useState(0)
  useLayoutEffect(() => {
    if (!learnCardEl) return
    const read = () => {
      const h = Math.round(learnCardEl.getBoundingClientRect().height)
      setLearnCardHeight((prev) => (prev === h ? prev : h))
    }
    read()
    const ro = new ResizeObserver(read)
    ro.observe(learnCardEl)
    return () => ro.disconnect()
  }, [learnCardEl])
  // Fill-in-the-blank: how the caption, English or translated, is fitted beside
  // the sentence (index into CAPTION_FIT_STEPS). Re-decided from the start on
  // the same occasions as the supporting lines.
  const [captionFitStep, setCaptionFitStep] = useState(0)
  useLayoutEffect(() => {
    setSupportLinesHidden(false)
    setCaptionFitStep(0)
  }, [currentCard?.card_id, definitionTranslationOpen, learnCardHeight])
  // Keep the soft keyboard raised across card transitions (mobile, fill-in-the-
  // blank / typing modes only). The pinned input itself never unmounts — its
  // wrapper is gated only by `isTextInputFormat`, which is stable across
  // same-mode cards. However, focus can be lost briefly when the underlying
  // query refetches, and Android IMEs hide the keyboard the moment the active
  // element stops being a text input. We refocus synchronously in a layout
  // effect (same tick as the card change) so the IME never sees an unfocused
  // state. ADR-011 still applies: we never toggle `readOnly` on the input —
  // input gating during the feedback window is handled by the onChange /
  // onKeyDown guards on the input element itself. See ADR-013.
  useLayoutEffect(() => {
    if (!isMobile) return
    const el = inputRef.current
    if (!el) return
    // Only refocus when the input is actually part of the current layout
    // (text-entry formats). For MC / listening / sentence-build, the input
    // wrapper isn't rendered so el is null and we've already returned above.
    if (document.activeElement !== el) {
      // Preserve the caret position when refocusing in retype state — the
      // user may be mid-correction; we don't want to jump to start.
      const len = el.value.length
      el.focus({ preventScroll: true })
      try { el.setSelectionRange(len, len) } catch { /* type=search may throw on some browsers */ }
    }
  }, [currentCard?.card_id, isMobile])

  // Desktop: focus the typed answer input when a new text-entry card arrives.
  // The mobile effect above handles keyboard continuity on mobile; this handles
  // desktop only. The critical case is MC→typed (and other non-text→typed):
  // the input is not in the DOM during the preceding card, so it mounts brand
  // new and unfocused. inputRef.current is null for MC/listening/sentence-build
  // (the input section only renders when isTextInputFormat is true), so the
  // null check acts as a format guard — no focus call for non-text formats.
  // typed→typed transitions are unaffected: the input stays mounted and focused.
  useLayoutEffect(() => {
    if (isMobile) return
    const el = inputRef.current
    if (!el || document.activeElement === el) return
    el.focus({ preventScroll: true })
  }, [currentCard?.card_id, isMobile])

  // Any input-hiding overlay currently open. When true, the focus-recovery
  // effects MUST NOT refocus the input — opening Definition / Reference, or
  // navigating to the previous-card view, intentionally dismisses the
  // keyboard (see openOverlayWithKeyboardDismiss), and re-raising it here
  // would race against the open animation and leave the IME on top of the
  // panel. `showingPrev` is included because Prompt H bug: blur → keyboard
  // retracts → viewportH jumps → keyboard-retract recovery refocused the
  // input → IME re-appeared on the previous-card view.
  const isAnyBottomSheetOpen =
    referenceOpen || prevDefinitionContent != null || showingPrev

  // Keyboard-retract recovery (mobile only). If the OS dismisses the soft
  // keyboard via a system event we cannot intercept (e.g. Android back button
  // closing the IME, iOS interruption), the visual viewport jumps back up by
  // ~roughly keyboard height. Synchronously refocusing the input in the same
  // useLayoutEffect tick re-raises the keyboard before the user notices the
  // unfocused state — same pattern as the card-transition refocus, no
  // setTimeout (per ADR-011/013). See ADR-013 update.
  const prevViewportRef = useRef(0)
  useLayoutEffect(() => {
    if (!isMobile) return
    const prev = prevViewportRef.current
    prevViewportRef.current = viewportH
    if (prev === 0 || viewportH <= prev + 80) return
    if (isAnyBottomSheetOpen) return
    const el = inputRef.current
    if (!el || document.activeElement === el) return
    const len = el.value.length
    el.focus({ preventScroll: true })
    try { el.setSelectionRange(len, len) } catch { /* type=search may throw */ }
  }, [viewportH, isMobile, inputRef, isAnyBottomSheetOpen])

  // Result-state refocus (mobile only). On wrong→retype transition the input
  // wrapper does not unmount, but useLearningSession previously used a
  // setTimeout to refocus — that deferred call opened the Android IME retract
  // window and dismissed the keyboard. Synchronous focus in a layout effect
  // keyed on resultState keeps focus continuous through every feedback
  // transition (none → wrong → retype → next-card). See ADR-014.
  useLayoutEffect(() => {
    if (!isMobile) return
    if (resultState !== 'retype' && resultState !== 'none') return
    // Do not refocus while a keyboard-hiding overlay is up (Definition,
    // Reference, previous-card view). The user is not looking at the input.
    if (isAnyBottomSheetOpen) return
    const el = inputRef.current
    if (!el || document.activeElement === el) return
    const len = el.value.length
    el.focus({ preventScroll: true })
    try { el.setSelectionRange(len, len) } catch { /* type=search may throw */ }
  }, [resultState, isMobile, inputRef, isAnyBottomSheetOpen])

  // Soft-keyboard state used to be tracked here to conditionally tighten the
  // header / progress / Previous-button rows when the keyboard appeared. That
  // toggling caused the entire layout above the input to shift the moment the
  // user dismissed the IME (Android Back, iOS interrupt). The fix: apply the
  // compact mobile spacing PERMANENTLY (top-bar padding, progress-bar margin,
  // card-zone padding) and hide the Previous button on mobile unconditionally.
  // Definition sheet and reference panel both dismiss the keyboard before
  // opening (see openOverlayWithKeyboardDismiss), so they still sit flush at
  // bottom:0 without keyboard-state tracking.

  // Set synchronously immediately before any deliberate `inputRef.current
  // .blur()` call (openOverlayWithKeyboardDismiss below) so the blur-recovery
  // handler on the input (ADR-014 caret amendment §5) can tell an intentional
  // keyboard-dismiss apart from an unwanted focus loss. `open()` triggers a
  // state update (e.g. setReferenceOpen) that only takes effect on the next
  // render, but `.blur()` fires the DOM `blur` event — and therefore the
  // onBlur handler closure, which still sees the OLD isAnyBottomSheetOpen —
  // synchronously, in the same tick. The ref sidesteps that staleness.
  const intentionalBlurRef = useRef(false)

  // Helper: dismiss the soft keyboard before opening a bottom-sheet overlay
  // (Definition / Reference). Without this, the overlay slides up but the
  // keyboard stays on top of it, hiding the panel content. After the overlay
  // closes we intentionally do NOT auto-refocus — the user taps the input
  // themselves to resume typing (avoids races with the close animation).
  const openOverlayWithKeyboardDismiss = useCallback((open: () => void) => {
    if (isMobile) {
      intentionalBlurRef.current = true
      inputRef.current?.blur()
    }
    open()
  }, [isMobile, inputRef])

  // Blur-recovery refocus (ADR-014 caret amendment §5, closes the §6 gap).
  // Card-change refocus, keyboard-retract recovery, and result-state refocus
  // (above) cover every focus-loss path EXCEPT an in-page blur that isn't
  // paired with a viewport change — e.g. a tap on card content while the
  // keyboard stays raised. The `blur` event fires synchronously, so handling
  // it directly (no useEffect/useLayoutEffect indirection) refocuses in the
  // same tick, before the Android IME gets a window to retract. Skipped when
  // the blur was deliberate (intentionalBlurRef, set by
  // openOverlayWithKeyboardDismiss) or a bottom sheet legitimately owns focus
  // (ADR-013 §3 — focus returns to the input on sheet close, not here).
  const handleAnswerInputBlur = useCallback(() => {
    setIsInputFocused(false)
    if (intentionalBlurRef.current) {
      intentionalBlurRef.current = false
      return
    }
    if (!isMobile || isAnyBottomSheetOpen) return
    // No isTextInputFormat check needed: the input only exists in the DOM
    // (and can therefore only blur) while that format is active. If the
    // format changed in the same tick as the blur, inputRef.current is
    // already null or points at the next card's element — either way `el`
    // below reflects current reality, same guard pattern as the other
    // refocus effects.
    const el = inputRef.current
    if (!el) return
    const { selectionStart, selectionEnd } = el
    el.focus({ preventScroll: true })
    try { el.setSelectionRange(selectionStart ?? el.value.length, selectionEnd ?? el.value.length) } catch { /* type=search may throw */ }
  }, [isMobile, isAnyBottomSheetOpen, inputRef])

  // Mirror the native input's caret + horizontal scroll into overlay state
  // (ADR-014 caret amendment §2). Called from every event that can move the
  // native selection — arrow keys, drag-handle drags, edits, and (after
  // `handleInputTap` has rewritten it) taps — PROVIDED the selection is
  // already collapsed; a range selection is intercepted first by
  // `handleSelectionEvent` below, which never lets a range reach this mirror.
  const syncCaretFromInput = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    setCaretIndex(el.selectionStart)
    setInputScrollLeft(el.scrollLeft)
  }, [inputRef])

  // THE single shared glyph-width source (ADR-014 caret amendment correction,
  // 2026-07-15). The synthetic-caret pixel offset (index → x, below), the
  // tap-to-index mapping (x → index, `indexForClientX`), and the range-
  // collapse target (also `indexForClientX`) all measure through this one
  // function, so a tap/press between two visible glyphs can never yield an
  // index whose rendered caret sits somewhere else — placement and drawing
  // share one definition of glyph widths. The font string is byte-identical
  // to the overlay text and the input's computed font: weight 400, fixed
  // 19px (mobile) / 20px (desktop) — the answer input is NOT on the ADR-012
  // prompt font ladder, so there is a single size to keep in parity —
  // `--font-content` family, normal letter-spacing. `ANSWER_PAD_LEFT` matches
  // the overlay's left content inset so the two coordinate systems share an
  // origin.
  const ANSWER_PAD_LEFT = isMobile ? 0 : 20
  const measureAnswerWidth = useCallback((text: string): number => {
    if (!text) return 0
    const canvas = measureCanvasRef.current ?? (measureCanvasRef.current = document.createElement('canvas'))
    const ctx = canvas.getContext('2d')
    if (!ctx) return 0
    const fontFamily = getComputedStyle(document.documentElement).getPropertyValue('--font-content').trim() || 'serif'
    ctx.font = `400 ${isMobile ? 19 : 20}px ${fontFamily}`
    return ctx.measureText(text).width
  }, [isMobile])

  // Pixel offset of the synthetic caret from the overlay's left content edge
  // (ADR-014 caret amendment §2/§3). Width of the text BEFORE the caret, minus
  // the native scroll position so an overflowed long term stays aligned.
  const caretOffsetPx = useMemo(() => {
    if (!isInputFocused) return 0
    const pos = caretIndex == null ? userInput.length : Math.min(caretIndex, userInput.length)
    return ANSWER_PAD_LEFT + measureAnswerWidth(userInput.slice(0, pos)) - inputScrollLeft
  }, [isInputFocused, ANSWER_PAD_LEFT, caretIndex, userInput, inputScrollLeft, measureAnswerWidth])

  // clientX → character index, through the shared glyph-width source. Used
  // both for direct tap placement (`handleInputTap`) and as the deliberate
  // collapse target when a range selection needs collapsing near a recent
  // press (`collapseSelectionIfRange`). Mapping: convert clientX to a
  // text-content coordinate (tap distance from the input's content-left
  // edge, plus the native horizontal scroll so overflowed/scrolled terms map
  // correctly), then pick the character boundary whose cumulative width is
  // nearest that x — the same nearest-boundary rule the browser itself uses.
  // Terms are short (tens of chars at most), so an O(n) prefix-measurement
  // scan is cheap.
  const indexForClientX = useCallback((clientX: number, el: HTMLInputElement): number => {
    const rect = el.getBoundingClientRect()
    const targetX = (clientX - rect.left) - ANSWER_PAD_LEFT + el.scrollLeft
    const len = userInput.length
    let best = 0
    let bestDist = Infinity
    for (let i = 0; i <= len; i++) {
      const d = Math.abs(measureAnswerWidth(userInput.slice(0, i)) - targetX)
      if (d < bestDist) { bestDist = d; best = i }
    }
    return best
  }, [ANSWER_PAD_LEFT, userInput, measureAnswerWidth])

  // App-owned tap → character-index placement (ADR-014 caret amendment
  // correction, 2026-07-15). Runs on `onClick`, i.e. AFTER the browser has
  // already (mis)placed the native caret using bullet-glyph metrics — so this
  // write wins deterministically. It does NOT preventDefault (the OS insertion
  // handle still spawns) and does NOT move focus. Blur-recovery may have
  // re-pinned the selection to end just before this click in the Android
  // blur-on-tap ordering; because we compute the index purely from the tap
  // x-coordinate (not from the possibly-stale `selectionStart`), that re-pin is
  // simply overwritten here.
  const handleInputTap = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    const el = inputRef.current
    if (!el) return
    const best = indexForClientX(e.clientX, el)
    try { el.setSelectionRange(best, best) } catch { /* type=search may throw */ }
    // Mirror immediately — don't wait for the trailing selectionchange. Reading
    // scrollLeft after setSelectionRange picks up any auto-scroll it triggered
    // to keep the caret in view.
    setCaretIndex(best)
    setInputScrollLeft(el.scrollLeft)
  }, [inputRef, indexForClientX])

  // Last pointerdown position on the answer input (ADR-014, second
  // correction, 2026-07-15). Read-only bookkeeping — the listener never calls
  // preventDefault and never touches focus/selection itself. Used solely to
  // pick a deliberate collapse target ("the boundary nearest the user's
  // press") when a long-press produces a range selection; a `t` timestamp
  // guards against using a stale press from an earlier, unrelated tap (e.g.
  // a keyboard-toolbar "Select all" triggered long after the last touch).
  const lastPressRef = useRef<{ x: number; t: number } | null>(null)
  const handleInputPointerDown = useCallback((e: React.PointerEvent<HTMLInputElement>) => {
    lastPressRef.current = { x: e.clientX, t: performance.now() }
  }, [])

  // Reentrancy guard for collapseSelectionIfRange (below) — see its comment.
  const isCollapsingSelectionRef = useRef(false)

  // Collapse any non-collapsed (range) selection on the answer input to a
  // single caret (ADR-014, second correction, 2026-07-15). This input must
  // never hold a range selection: selection painting on a `type="password"`
  // field renders the native bullet glyphs over the transparent-text overlay
  // (the overlay's transparent color is ignored by selection highlighting),
  // and a range also spawns a Translate/Select-all context menu with no
  // purpose on this field. Reacts to selection STATE rather than
  // intercepting the gesture that produced it (long-press, keyboard-toolbar
  // Select all, or anything else) — no preventDefault on pointer events.
  // Collapse target: the boundary nearest the user's most recent press,
  // computed through the same `indexForClientX` the tap handler uses, so it
  // lands where the finger actually was rather than at an arbitrary edge of
  // the selection. Falls back to `selectionEnd` when there's no recent press
  // to anchor to (keyboard-driven Select all, or a stale/expired press).
  // Returns whether a range was found and collapsed, so callers can skip a
  // redundant plain mirror in the same tick.
  const collapseSelectionIfRange = useCallback((): boolean => {
    if (isCollapsingSelectionRef.current) return false
    const el = inputRef.current
    if (!el) return false
    const { selectionStart, selectionEnd } = el
    if (selectionStart == null || selectionEnd == null || selectionStart === selectionEnd) return false
    isCollapsingSelectionRef.current = true
    try {
      const press = lastPressRef.current
      const pressIsRecent = press != null && performance.now() - press.t < 1500
      const target = pressIsRecent ? indexForClientX(press.x, el) : selectionEnd
      try { el.setSelectionRange(target, target) } catch { /* type=search may throw */ }
      setCaretIndex(target)
      setInputScrollLeft(el.scrollLeft)
    } finally {
      isCollapsingSelectionRef.current = false
    }
    return true
  }, [inputRef, indexForClientX])

  // Combined handler for every event that can reflect a NEW native selection
  // (as opposed to `handleInputTap`, which always WRITES a collapsed one):
  // collapse first if it's a range, otherwise just mirror the (already
  // collapsed) caret. Wired to `onSelect`/`onKeyUp` on the input and to the
  // document `selectionchange` listener below — never to `onClick`, which
  // has its own dedicated tap-to-index writer.
  const handleSelectionEvent = useCallback(() => {
    if (collapseSelectionIfRange()) return
    syncCaretFromInput()
  }, [collapseSelectionIfRange, syncCaretFromInput])

  // Android drag-handle moves and same-position taps don't always emit an
  // input-level `select` event, but they do fire document `selectionchange`.
  // Subscribe only while the answer input is focused so we're not reacting to
  // selection changes elsewhere (bottom-sheet text, etc.). This is also the
  // path that catches a long-press range selection landing (it fires
  // `selectionchange` even when no discrete `select` event does), so it is
  // wired to the same collapse-or-mirror handler as `onSelect`.
  useEffect(() => {
    if (!isInputFocused) return
    document.addEventListener('selectionchange', handleSelectionEvent)
    return () => document.removeEventListener('selectionchange', handleSelectionEvent)
  }, [isInputFocused, handleSelectionEvent])

  // Previous-card transition (Prompt H). Both the live card stage and the
  // previous-card overlay translate together using the SAME transition
  // config, so neither sits stationary while the other slides over. Driving
  // both with the same `showingPrev` flag (rather than two independent
  // AnimatePresence enter/exit pairs) guarantees the same frame, same easing,
  // and same duration. prefers-reduced-motion collapses to an instant swap.
  const reduceMotionPrev = useReducedMotion()
  const PREV_SLIDE_TRANSITION = reduceMotionPrev
    ? { duration: 0 } as const
    : { type: 'tween' as const, duration: 0.22, ease: 'easeOut' as const }

  // ── Retype reveal red→ghost phase (ADR-014 amendment, 2026-07-14) ──────────
  // Two-phase signal for the wrong-answer reveal shown in the input overlay:
  //   'red'   — full-strength danger colour, the in-the-moment error.
  //   'ghost' — neutral --text-secondary at RETYPE_GHOST_OPACITY, a standing
  //             placeholder that reads as "now type this word yourself".
  // Lifecycle: the phase is armed to 'red' whenever we are NOT in a wrong
  // lifecycle, so the next wrong answer always starts red. On entering a wrong
  // lifecycle a single read-pause timer flips it to 'ghost'. The timer is never
  // restarted (guarded by the ref + current phase), so the 'wrong'→'retype'
  // state change does not replay red, and — critically — backspacing the input
  // back to empty restores the ghost directly rather than re-running the red
  // phase (the effect does not depend on userInput, so typing/erasing never
  // touches the timer). See DECISIONS.md ADR-014 §9.
  const [retypeGhostPhase, setRetypeGhostPhase] = useState<'red' | 'ghost'>('red')
  const ghostPauseTimer = useRef<number | null>(null)
  useEffect(() => {
    const inWrongLifecycle =
      (resultState === 'wrong' || resultState === 'retype')
      && lastResult != null && !lastResult.is_correct

    if (!inWrongLifecycle) {
      // Correct answer / advanced / new card — cancel any pending fade and arm
      // red for the next wrong answer.
      if (ghostPauseTimer.current !== null) {
        clearTimeout(ghostPauseTimer.current)
        ghostPauseTimer.current = null
      }
      setRetypeGhostPhase('red')
      return
    }
    // Inside a wrong lifecycle. Start the read-pause exactly once. The guard on
    // phase==='red' + a null timer means the 'wrong'→'retype' transition (which
    // re-runs this effect) does not restart the pause. Under reduced motion the
    // pause is unchanged; only the CSS fade at the render site is dropped.
    if (retypeGhostPhase === 'red' && ghostPauseTimer.current === null) {
      ghostPauseTimer.current = window.setTimeout(() => {
        setRetypeGhostPhase('ghost')
        ghostPauseTimer.current = null
      }, RETYPE_READ_PAUSE_MS)
    }
  }, [resultState, lastResult, retypeGhostPhase])
  useEffect(() => () => {
    if (ghostPauseTimer.current !== null) clearTimeout(ghostPauseTimer.current)
  }, [])

  // ── Retype coach mark (one-time, first wrong typed answer) ───────────────────
  // Persisted via RETYPE_COACH_KEY in localStorage so it never reappears after
  // the user dismisses it (either by tapping "Got it" or by starting to type).
  // Existing users without the key see it on their next wrong typed answer.
  const [showRetypeCoach, setShowRetypeCoach] = useState(false)
  const dismissRetypeCoach = useCallback(() => {
    setShowRetypeCoach(false)
    localStorage.setItem(RETYPE_COACH_KEY, '1')
  }, [])
  // Show: first time this device enters the retype state (text-input formats only;
  // MC/listening/sentence-build never reach resultState='retype').
  useEffect(() => {
    if (resultState !== 'retype' || lastResult == null || lastResult.is_correct) return
    if (!localStorage.getItem(RETYPE_COACH_KEY)) setShowRetypeCoach(true)
  }, [resultState, lastResult])
  // Auto-hide (no localStorage write) when the card advances without a tap dismiss.
  useEffect(() => {
    if (resultState === 'none' || resultState === 'correct') setShowRetypeCoach(false)
  }, [resultState])
  // Dismiss (writes localStorage) the moment the user starts typing the retype answer.
  useEffect(() => {
    if (showRetypeCoach && userInput.length > 0) dismissRetypeCoach()
  }, [userInput, showRetypeCoach, dismissRetypeCoach])

  // Previous-card swipe gesture (mobile only — Prompt F). On mobile the
  // dedicated Previous-card button is hidden (Prompt A reclaim of vertical
  // space); a horizontal right-swipe on the card area takes its place.
  // Reuses the existing `setShowingPrev` action — no duplicate navigation
  // logic. Disabled when there is no previous card, matching the desktop
  // button's disabled state.
  //
  // Gesture filter: requires (a) ≥ 60px positive horizontal delta, (b) the
  // horizontal delta dominating the vertical delta by ≥ 2×. Taps, vertical
  // scrolls, and short drags fall through — so taps on Definition / Reference
  // triggers (which live inside the card zone) keep their normal onClick
  // behavior. The pinned answer input lives in row 3 outside this handler,
  // so focused-input swipes never fire previous-card navigation. Swipe-left
  // is intentionally a no-op (answer submission is the only way forward).
  const SWIPE_DX_MIN = 60
  const SWIPE_RATIO_MIN = 2
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null)
  const onCardZoneTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile || !prevCard) return
    const t = e.touches[0]
    if (!t) return
    swipeStartRef.current = { x: t.clientX, y: t.clientY }
  }, [isMobile, prevCard])
  const onCardZoneTouchEnd = useCallback((e: React.TouchEvent) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    if (!isMobile || !prevCard || !start) return
    const t = e.changedTouches[0]
    if (!t) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (dx >= SWIPE_DX_MIN && Math.abs(dx) >= Math.abs(dy) * SWIPE_RATIO_MIN) {
      // Dismiss the IME before the previous-card overlay slides in, otherwise
      // the keyboard covers the lower half of the review view (Prompt G —
      // same helper as Definition / Reference overlays from Prompt 1).
      openOverlayWithKeyboardDismiss(() => setShowingPrev(true))
    }
  }, [isMobile, prevCard, setShowingPrev, openOverlayWithKeyboardDismiss])
  const onCardZoneTouchCancel = useCallback(() => {
    swipeStartRef.current = null
  }, [])

  // Mobile prompt-zone font sizing (mobile only — desktop uses fixed sizes).
  // A ResizeObserver on the prompt element (see useLadderFontSize) re-measures
  // until the element's geometry goes quiet, so the ladder adapts when the
  // keyboard raises and shrinks the element — however long that takes —
  // including on WebViews where visualViewport.resize does not fire (ADR-027).
  // After settling it re-opens whenever the element's height changes again.
  //
  // ADR-100 D7 (re-specified 2026-09-21): the reveal replaces the prompt's text
  // in both formats — the Definition in FROM MEMORY, the example sentence in
  // fill-in-the-blank — so the text in the box changes and the ladder must
  // measure what is actually rendered. The reveal state is part of the key:
  // opening and closing re-runs the walk.
  const revealKey = `${currentCard?.card_id ?? ''}|${definitionTranslationOpen ? 'tr' : 'src'}`
  // The step before shrinking, under the reveal only: hide the supporting lines
  // (form line, term translation) to give the translated text their height.
  // Mobile only — on desktop the card grows to its content and nothing needs
  // freeing. Returns false once there is nothing left to free, so the ladder
  // then shrinks and, at its floor, scrolls.
  // (`definitionTranslationOpen` stands for the reveal being on: the control
  // can only open it where it is available, and it closes on card change.)
  const freeSupportLines = useCallback(() => {
    if (!isMobile || !definitionTranslationOpen || supportLinesHidden) return false
    setSupportLinesHidden(true)
    return true
  }, [isMobile, definitionTranslationOpen, supportLinesHidden])
  // Fill-in-the-blank only, at the ladder's floor and before any scroll: give
  // the sentence more room by shrinking the caption, and only as the last step
  // clamp it. Each step re-walks the sentence from the base, so it ends at the
  // largest size at which both fit whole — showing both whole outranks the
  // sentence's size (owner's rule, 2026-09-21), with RU on or off. With RU off
  // nothing else is freed for it: the supporting lines stay. On a card with no
  // caption a step changes nothing on screen, and the walk ends where it would
  // have.
  const shrinkCaption = useCallback(() => {
    if (!isMobile || captionFitStep >= CAPTION_FIT_STEPS.length - 1) return false
    setCaptionFitStep(captionFitStep + 1)
    return true
  }, [isMobile, captionFitStep])
  const fibLadder = useLadderFontSize(fibContentRef, PROMPT_FONT_LADDER, PROMPT_FONT_LADDER_BASE_IDX, revealKey, freeSupportLines, shrinkCaption)
  const definitionLadder = useLadderFontSize(definitionContentRef, PROMPT_FONT_LADDER, PROMPT_FONT_LADDER_BASE_IDX, revealKey, freeSupportLines)
  // Scroll + fade follow what is on screen now, not the ladder's verdict alone
  // (ADR-100 D7): a caption's "More" can take height from the box between walks.
  const fibOverflowing = useRenderedOverflow(fibContentRef, revealKey)
  const definitionOverflowing = useRenderedOverflow(definitionContentRef, revealKey)
  const fibScrolls = fibLadder.shouldScroll || fibOverflowing.overflowing
  const definitionScrolls = definitionLadder.shouldScroll || definitionOverflowing.overflowing


  const handleBack = useCallback(() => {
    const from = sqState?.from
    if (from) navigate(from)
    else if (sqState?.sessionId) navigate('/home')
    else if (deckId) navigate(`/decks/${deckId}`)
    else if (courseId) navigate(`/courses/${courseId}`)
    else navigate(-1)
  }, [sqState, deckId, courseId, navigate])

  // Android back-button intercept (mobile only). Push a sentinel history state
  // on mount; on popstate (back press), exit the Learn session rather than
  // leave the user on the Learn page with the keyboard hidden. The IME-close
  // half of the Android back-press is system-level (no JS hook), but the
  // viewport-retract refocus effect re-raises the keyboard if it slipped down.
  // See ADR-013.
  useEffect(() => {
    if (!isMobile) return
    window.history.pushState({ learnSentinel: true }, '')
    const onPopState = () => { handleBack() }
    window.addEventListener('popstate', onPopState)
    return () => { window.removeEventListener('popstate', onPopState) }
  }, [isMobile, handleBack])

  // ── Render: loading ──────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: 0 }}>{tr.learn.startingSession}</p>
      </div>
    )
  }

  // ── Render: error ────────────────────────────────────────────────────────────
  if (phase === 'error') {
    if (errorMsg) console.error('[LearnPage] session error:', errorMsg)
    const displayMsg = errorMsg || tr.learn.sessionError
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>⚠️</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360 }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{tr.learn.somethingWrong}</p>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
            {displayMsg}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleBack}
            style={{ padding: '12px 20px', borderRadius: 50, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer', minHeight: 44 }}
          >
            {tr.learn.back}
          </button>
          <button
            // ADR-103 Decision 7: repeat the recovery for the same entry point —
            // never a reload, which would read a refused session id back.
            onClick={retrySession}
            style={{ padding: '12px 24px', borderRadius: 50, border: 'none', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}
          >
            {tr.learn.tryAgain}
          </button>
        </div>
      </div>
    )
  }

  // ── Render: empty (no cards due) ─────────────────────────────────────────────
  if (phase === 'empty') {
    // Format next_due_at (ISO string from backend, naive UTC) into a human-readable label.
    let nextDueLabel = tr.learn.checkBackLater
    if (nextDueAt) {
      const due = new Date(nextDueAt + 'Z') // append Z so Date treats it as UTC
      const now = Date.now()
      const diffMs = due.getTime() - now
      if (diffMs <= 0) {
        nextDueLabel = tr.learn.cardsReadyNow
      } else if (diffMs < 60 * 60 * 1000) {
        const mins = Math.ceil(diffMs / 60_000)
        nextDueLabel = tr.learn.nextCardMins(mins)
      } else if (diffMs < 24 * 60 * 60 * 1000) {
        const hrs = Math.ceil(diffMs / 3_600_000)
        nextDueLabel = tr.learn.nextCardHours(hrs)
      } else {
        nextDueLabel = tr.learn.nextCardDate(due.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }))
      }
    }
    const backLabel = sqState?.sessionId
      ? tr.learn.backToHome
      : deckId ? tr.learn.backToDeck
      : courseId ? tr.learn.backToCourse
      : tr.learn.back
    // Quick prep with nothing to practise (ADR-112 Decision 3 and Implementation
    // note 1): prep takes only words already answered and not yet mastered, and
    // never falls back to an ordinary lesson. "All caught up" with a review time
    // would be untrue here — say why, and what to do instead.
    if (sqState?.is_prep) {
      return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: '0 24px' }}>
          <DeckStatusBadge status="practice" size="md" />
          <div style={{ textAlign: 'center', maxWidth: 320 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>{tr.learn.practiceEmptyTitle}</h2>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: 0 }}>{tr.learn.prepEmptyBody}</p>
          </div>
          <button
            onClick={handleBack}
            style={{ padding: '12px 28px', borderRadius: 50, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer', minHeight: 44 }}
          >
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: '0 24px' }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>{tr.learn.allCaughtUp}</h2>
          <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: 0 }}>{nextDueLabel}</p>
        </div>
        <button
          onClick={handleBack}
          style={{ padding: '12px 28px', borderRadius: 50, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer', minHeight: 44 }}
        >
          {backLabel}
        </button>
      </div>
    )
  }

  // ── Render: complete ─────────────────────────────────────────────────────────
  if (phase === 'complete') {
    const todayCards = progressSummary?.today_resolutions
    const dailyGoal = progressSummary?.daily_card_goal
    const isPrep = !!prepStats
    // For SQ sessions, use the progress summary to detect if any cards are left.
    // For source sessions, we always offer Continue and let the session handle NO_DUE_CARDS.
    const sqExhausted = !!sqState?.sessionId && continueExhausted
    const sqCardsAvailable = !sqState?.sessionId ||
      (progressSummary?.due_review_count ?? 0) + (progressSummary?.new_cards_available ?? 0) > 0
    const continueUnavailableReason = !isPrep && (sqExhausted || !sqCardsAvailable)
      ? 'All your cards are caught up for now!'
      : undefined
    return (
      <LearningComplete
        stats={stats}
        wordsLearnedToday={wordsLearnedToday}
        todayCards={todayCards}
        dailyCardGoal={dailyGoal}
        streakDays={progressSummary?.streak_days}
        onContinueLearning={continueUnavailableReason ? undefined : handleContinueLearning}
        continueUnavailableReason={continueUnavailableReason}
        onGoHome={() => navigate('/home')}
        mistakes={mistakes}
        blockStats={blockStats}
        prepStats={prepStats}
        isPractice={isPractice}
        milestone={milestone}
        totalWordsKnown={postSessionWordsKnown ?? progressSummary?.words_known_count}
      />
    )
  }

  // ── Render: no card yet (intermediate fetch after session created) ────────────
  if (!currentCard) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: 0 }}>{tr.learn.loadingCard}</p>
      </div>
    )
  }

  // ── Active study UI ──────────────────────────────────────────────────────────

  // Resolve fill-in-the-blank data: Term side provides the answer, Example side provides context
  const termSide = findTermSide(currentCard.other_sides)
  // ADR-104: the card's example and definition are its VALUE sides — the first
  // non-blank side of the kind — so a blank field in front never empties the
  // sentence, the hint or the form line.
  const exampleSide = learnExampleSide(currentCard)
  // When "Definition" is the session prompt (the default), it is excluded from
  // other_sides and lives in prompt instead; learnDefinitionSide reads both.
  const definitionSide = learnDefinitionSide(currentCard)
  // Grammatical-form hint for the pinned zone (P-L3). Covers all prompt modes.
  // Read from the definition value side (ADR-104 Decision 5).
  const formHint = definitionSide?.form ?? currentCard.prompt.form ?? null
  const definitionContent: string | null = definitionSide?.content ?? null

  // Exercise format from backend — determines what context to show based on mastery level.
  // "multiple_choice"      — mastery 1 (variety slot) with distractors: 4 tappable option buttons
  // "fill_blank_supported" — legacy mastery 1: FiB + definition + translation always on
  // "fill_blank"           — mastery 2-3: FiB, definition in tab, user translation preference
  // "definition_only"      — mastery 4-5 or no example: definition only, hint allowed
  // "definition_strict"    — mastery 6+ or no example: definition only, no hint
  // Frontend defaults to "fill_blank" for cards from older sessions missing this field.
  const exerciseFormat = currentCard.exercise_format ?? 'fill_blank'
  // Fall back to fill_blank when MC options couldn't be built (e.g. degenerate card data).
  // Fall back from listening to fill_blank when TTS is unavailable in this browser.
  // Fall back from sentence_build to fill_blank when no sentence_words were provided.
  // Fall back from intro (removed format) to fill_blank silently.
  const effectiveFormat =
    // `intro` is a removed format; keep the defensive fallback in case a stale
    // backend still emits it (the current type no longer includes it).
    (exerciseFormat as string) === 'intro' ? 'fill_blank'
    : (exerciseFormat === 'multiple_choice' && mcOptions.length < 4) ? 'fill_blank'
    : (exerciseFormat === 'listening' && !isSpeechSupported) ? 'fill_blank'
    : (exerciseFormat === 'sentence_build' && !currentCard.sentence_words?.length) ? 'fill_blank'
    : exerciseFormat

  const correctTerm = termSide?.content ?? ''
  const fibData = termSide && exampleSide
    ? buildFillInBlank(exampleSide.content, termSide.content)
    : null

  // Two prompt modes:
  //  'fib'        — example exists and a blank can be produced: show sentence with blank
  //  'definition' — no blank possible (no example, irregular inflection, format forces definition)
  //
  // When buildFillInBlank returns null (irregular form such as carry→carries or break→broke,
  // ~57 system-deck cards), we fall back to 'definition' even when an exampleSide exists.
  // Showing the example as plain text in this case would expose the answer in the sentence
  // before the learner has typed it (Symptom 6, render-safety guard).
  const promptMode: 'fib' | 'definition' =
    (effectiveFormat === 'definition_only' || effectiveFormat === 'definition_strict')
      ? 'definition'
      : fibData ? 'fib'
      : 'definition'

  // Scaffolding visibility — backend is single source of truth (ADR-043).
  // Default to true for backward compat with sessions created before these flags existed.
  const showTranslation = currentCard.show_translation ?? true
  const showDefinition = currentCard.show_definition ?? true
  const showForm = currentCard.show_form ?? true

  // ADR-100 D7, re-specified by the owner 2026-09-21 (ADR amendment pending,
  // superseding Decision 4's "the blanked sentence never swaps" carve-out).
  // Pressing RU replaces each English text with its translation in its own
  // place; pressing it again restores the card exactly.
  //  · FROM MEMORY: the Definition is the prompt, and its text is replaced.
  //  · fill-in-the-blank: the example sentence is replaced by its translation,
  //    whole, with the word in place and no blank — while RU is on there is no
  //    blank on the card (the owner's decision: read the Russian, press RU
  //    again, type into the English). The Definition caption is replaced by
  //    its translation too.
  // Each text is read from the very side on screen, so a translation always
  // translates what the learner sees. Fresh rows for the reader's
  // source_language only; no fallback language. The whole reveal rides on
  // `show_translation` (hidden at level 7 and with the preference off).
  const readerLanguage = user?.source_language ?? null
  const shownDefinitionSide = promptMode === 'definition'
    ? currentCard.prompt
    : definitionContent && showDefinition
    ? (definitionSide ?? currentCard.prompt)
    : null
  const definitionTranslation =
    (readerLanguage && shownDefinitionSide?.translations?.[readerLanguage]?.trim()) || null
  const exampleTranslation = promptMode === 'fib'
    ? (readerLanguage && exampleSide?.translations?.[readerLanguage]?.trim()) || null
    : null
  const definitionTranslationAvailable =
    (definitionTranslation != null || exampleTranslation != null) && showTranslation
  const swapOn = definitionTranslationAvailable && definitionTranslationOpen
  // Under the reveal, the lines that step aside to make room (freeSupportLines).
  const hideSupportLines = swapOn && supportLinesHidden
  const toggleDefinitionTranslation = () => {
    setDefinitionTranslationOpen((v) => !v)
    // A pointer never moved focus (the control prevents it). Keyboard
    // activation did — hand it back so typing continues and, on mobile, the
    // keyboard stays up (ADR-013).
    const el = inputRef.current
    if (el && document.activeElement !== el) {
      const len = el.value.length
      el.focus({ preventScroll: true })
      try { el.setSelectionRange(len, len) } catch { /* type=search may throw */ }
    }
  }

  // In fib mode: Definition is shown as an inline hint below the sentence,
  // so remove it from tabs to avoid duplication. Also remove Term (answer) and Example (primary prompt) —
  // fib mode's own Example exclusion is about not duplicating the prompt (already shown blanked),
  // not about hiding an answer, so it stays a label match here, unchanged.
  // In definition mode: Definition IS the prompt, and an Example side (if the card has one) still
  // contains the full, un-blanked answer sentence — see definitionModeSupplementarySides for why that
  // exclusion is by kind, not by label, and why it also covers the "Show All" overlay below.
  // Image sides are always hidden in Learn mode — they reveal the answer visually.

  // "Translation" is excluded from tabs — it's shown inline below the definition/example prompt.
  const tabSides = promptMode !== 'definition'
    ? currentCard.other_sides.filter(
        (s) => isAllowedSide(s) && !isTermSide(s) && s.label !== 'Example' && s.label !== 'Definition' && s.label !== 'Translation' && !isImageSide(s),
      )
    : definitionModeSupplementarySides(currentCard.other_sides, isTermSide)

  const activeSide = tabSides[activeTab]

  // "Show All" overlay: exclude Term (it's the answer during an active session) + image sides.
  // Definition mode reuses the same kind-based Example exclusion as tabSides above, for the same
  // reason: a card can carry an Example-kind side under any allowed label, and this overlay must
  // never offer it any more than the tabs do.
  const allSides = [
    ...(promptMode !== 'definition' && exampleSide
      ? [{ label: 'Example', content: exampleSide.content }]
      : [currentCard.prompt]),
    // Translation is shown inline, exclude from "Show All" overlay to avoid duplication
    ...(promptMode !== 'definition'
      ? currentCard.other_sides.filter((s) => isAllowedSide(s) && !isTermSide(s) && s.label !== 'Example' && s.label !== 'Translation' && !isImageSide(s))
      : definitionModeSupplementarySides(currentCard.other_sides, isTermSide)),
  ]

  // Show updated mastery level from result, otherwise current card's level
  const displayMastery = (lastResult && resultState !== 'none') ? lastResult.mastery_level : currentCard.mastery_level

  // In-blank reveal mode (FiB mode). Spec ADR-014 + Prompt C:
  //  • none           — empty input, no result yet → underline placeholder.
  //  • full / success — correct answer, green, original casing per occurrence.
  //  • full / danger  — wrong or retype, RED, full term revealed with original
  //    casing per occurrence (the user retypes from this reveal). The legacy
  //    "c______" masked hint is no longer used at submission time; only the
  //    full reveal is shown for both correct and incorrect outcomes.
  // FillInBlankSentence renders every coordinated blank with this same state.
  const correctAnswerText = lastResult?.correct_answer ?? ''
  const blankReveal: 'none' | 'masked' | 'full' =
    (resultState === 'correct' || resultState === 'wrong' || resultState === 'retype') && correctAnswerText
      ? 'full'
      : 'none'
  const blankRevealColor = resultState === 'correct' ? 'var(--success)' : 'var(--danger)'

  // Soft-reject first-letter hint (ADR-048 §7 amendment, 2026-07-15): rendered
  // inside the existing blank rather than the input placeholder, so it no
  // longer competes with the explanation text for width.
  //
  // Deliberately NOT gated on resultState === 'soft_reject': the onChange
  // handler drops resultState to 'none' the instant the learner types a
  // character (so Enter/submit fires from 'none' again, per ADR-048 §1
  // unbounded retries) — that transition existed for the input placeholder,
  // which must hide while typing. The blank has no such conflict, so the
  // hint stays keyed to `lastResult` itself (unchanged by typing, only ever
  // replaced by the next submit's result or reset to null on card advance)
  // and only clears on an actual resolution (correct/wrong/retype) — i.e. it
  // persists across typing and clearing, and disappears only when the card
  // resolves.
  const blankHintLetter =
    lastResult && 'is_soft_reject' in lastResult && lastResult.is_soft_reject
    && resultState !== 'correct' && resultState !== 'wrong' && resultState !== 'retype'
      ? lastResult.first_letter
      : null

  // Progress: resolution events completed / fixed session size.
  // resolutionCount increments immediately on each correct answer or accepted retype.
  // totalCards is fixed between entries: it grows only when a resume takes in new
  // words (ADR-113), and the resume response carries the new value.
  const queueTotal = totalCards
  const done = resolutionCount

  // In-input overlay active when the ghost-text "correct answer" is showing.
  // Used to (a) hide the input's own text via color:transparent and (b) clear
  // the native placeholder so it never renders alongside the overlay. The
  // placeholder + overlay are mutually exclusive: when the input is empty
  // during retype after a wrong answer, ONLY the red ghost answer is visible;
  // as soon as the user types the overlay hides and the typed characters
  // appear; if the user erases back to empty, the overlay returns — the
  // native placeholder is never shown. See ADR-014.
  const overlayActive =
    resultState === 'correct'
    // Ghost (red correct answer) shows only while the input is empty — mirrors
    // the 'retype' branch. Once the user starts typing the overlay clears and
    // the typed characters become visible, same as in retype state.
    || (resultState === 'wrong' && userInput === '' && lastResult != null && !lastResult.is_correct)
    || (resultState === 'retype' && userInput === '' && lastResult != null && !lastResult.is_correct)

  // Input visual state
  const inputBorder = resultState === 'correct'
    ? 'var(--success)'
    : (resultState === 'wrong' || resultState === 'retype')
    ? 'var(--danger)'
    : resultState === 'soft_reject'
    ? 'var(--accent)'
    : 'var(--border)'
  // In-the-moment feedback tint via tokens (no hardcoded hex; dark-theme parity
  // from Prompt 3.2). Red here is a correct use — the input is showing a wrong
  // answer to retype.
  const inputBg = resultState === 'correct'
    ? 'var(--success-soft)'
    : (resultState === 'wrong' || resultState === 'retype')
    ? 'var(--danger-soft)'
    : resultState === 'soft_reject'
    ? 'var(--accent-soft)'
    : 'var(--bg-surface)'

  // Submit button active state (prototype spec, Prompt 3; parity fix Prompt 4):
  // accent + glow whenever the button is actionable (matches Enter key behaviour exactly).
  // The visual hint (grey when empty) is intentionally removed — an empty tap is a valid
  // "I don't know" submission, identical to pressing Enter with an empty input.
  const btnReady =
    resultState === 'retype' ||
    resultState === 'none' ||
    resultState === 'soft_reject'

  // Input + buttons render in the pinned bottom section for all text-entry formats
  const isTextInputFormat = effectiveFormat !== 'listening'
    && effectiveFormat !== 'sentence_build'
    && !(effectiveFormat === 'multiple_choice' && mcOptions.length === 4)

  // Multiple-choice options are also pinned to the bottom of the viewport so
  // their position does not shift with card height (anchors them to the same
  // zone where the text input lives in typing mode).
  const isMCFormat = effectiveFormat === 'multiple_choice' && mcOptions.length === 4

  // Bottom sheets always overlay — they never reshape the page layout.
  // Their fixed 60dvh height applies regardless of mode (typing/MC). The
  // sheet's own CSS handles its viewport-relative positioning.

  // Overlays + sheets are portaled to document.body so they sit OUTSIDE the
  // grid/flow of the page. This guarantees that opening a sheet never pushes
  // the card or the answer zone, and that the sheet stacks on top of every
  // grid row regardless of where in the tree the trigger lives.
  const overlays = (
    <>
      <WordReferencePanel
        term={prevReferenceTerm ?? (termSide?.content ?? '')}
        isOpen={referenceOpen}
        onClose={() => {
          setReferenceOpen(false)
          window.setTimeout(() => {
            setPrevReferenceTerm(null)
            setPrevReferenceSentence(undefined)
          }, 300)
        }}
        targetLanguage={user?.target_language ?? 'en'}
        sourceLanguage={user?.source_language ?? 'en'}
        sentence={prevReferenceTerm ? prevReferenceSentence : exampleSide?.content}
      />
      {isMobile && prevDefinitionContent != null && (
        <DefinitionBottomSheet
          isOpen={true}
          onClose={() => { setPrevDefinitionContent(null); setPrevDefinitionExample(undefined) }}
          definition={prevDefinitionContent}
          example={prevDefinitionExample}
          speak={speak}
          isSpeechSupported={isSpeechSupported}
        />
      )}
      {showAll && <ShowAllOverlay sides={allSides} onClose={() => setShowAll(false)} speak={speak} isSpeechSupported={isSpeechSupported} />}
      {/* Coupled slide for the previous-card overlay (Prompt H). The overlay
          translates from x:-100% → 0 on enter and 0 → -100% on exit, paired
          with the current-card stage which slides 0 → +100% / +100% → 0. Both
          motion.divs share `PREV_SLIDE_TRANSITION`, so neither sits still
          ("наезд"). AnimatePresence keeps the overlay mounted through its
          exit animation. The outer stage clips horizontally so neither side
          creates a horizontal scroll bar. */}
      <AnimatePresence>
        {showingPrev && prevCard && (
          <motion.div
            key="previous-card-overlay"
            initial={{ x: '-100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '-100%' }}
            transition={PREV_SLIDE_TRANSITION}
            style={{ position: 'fixed', inset: 0, zIndex: 90 }}
          >
            <PreviousCardOverlay
              card={prevCard}
              onClose={() => setShowingPrev(false)}
              speak={speak}
              isSpeechSupported={isSpeechSupported}
              isSpeaking={isSpeaking}
              showTranslations={showTranslations}
              onOpenReference={(term, sentence) => {
                openOverlayWithKeyboardDismiss(() => {
                  setPrevReferenceTerm(term)
                  setPrevReferenceSentence(sentence)
                  setReferenceOpen(true)
                })
              }}
              onOpenDefinition={(definition, example) => {
                openOverlayWithKeyboardDismiss(() => {
                  setPrevDefinitionContent(definition)
                  setPrevDefinitionExample(example)
                })
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {pendingGrammarNote && (
        <GrammarNoteCard
          note={pendingGrammarNote}
          onContinue={handleGrammarNoteContinue}
        />
      )}
      <RetypeCoachMark
        visible={showRetypeCoach}
        onDismiss={dismissRetypeCoach}
        isMobile={isMobile}
        title={tr.learn.retypeCoachTitle}
        body={tr.learn.retypeCoachBody}
        dismissLabel={tr.learn.retypeCoachDismiss}
      />
    </>
  )

  return (
    <div style={{
      // Stage: fixed full-viewport, overflow:hidden, so the current-card
      // motion.div can translate to x:+100% without producing a horizontal
      // scroll on body. The previous-card overlay (rendered via portal)
      // slides into this same viewport in lockstep — both children share
      // PREV_SLIDE_TRANSITION.
      position: 'fixed', inset: 0, overflow: 'hidden',
      background: 'var(--bg-page)',
    }}>
      {/* Sheets and overlays render via portal so they never participate in
          the grid's row sizing — opening one cannot push the card or options. */}
      {typeof document !== 'undefined' && createPortal(overlays, document.body)}
    <motion.div
      animate={{ x: showingPrev ? '100%' : '0%' }}
      transition={PREV_SLIDE_TRANSITION}
      style={{
      position: 'relative',
      height: viewportH > 0 ? viewportH : '100dvh',
      background: 'var(--bg-page)',
      // CSS grid hard-bounds the card zone (1fr row) between the header and
      // the answer zone (both auto). Card cannot grow into either neighbor.
      display: 'grid',
      gridTemplateRows: 'auto 1fr auto',
      justifyItems: 'center',
      overflow: 'hidden',
      width: '100%',
    }}>

      {/* ── Row 1 (auto): header ───────────────────────────────────────────── */}
      <div ref={headerRowRef} style={{
        width: '100%', maxWidth: 640,
        padding: '0 16px',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
      }}>

      {/* Top bar — permanently compact vertical padding on mobile so the 1fr
          card row reliably fits the three-line example case at base font on a
          360x640 viewport. The Back button keeps its 44px tap target; only the
          surrounding container padding is reduced. Layout MUST NOT change with
          keyboard state — see comment near the removed `keyboardUp` flag. */}
      <div style={{
        width: '100%', maxWidth: 640,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '0' : '16px 0 12px',
      }}>
        {/* Mobile: 40px tap target (slightly below the 44px DESIGN_SYSTEM
            minimum) to keep the top bar visibly slim; the Back action + hit
            area are preserved. Desktop keeps the full 44px + padding. */}
        <button
          onClick={handleBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, padding: isMobile ? '8px' : '12px 8px', minHeight: isMobile ? 40 : 44 }}
        >
          {tr.learn.back}
        </button>
        {/* Session context label — shown for topic-train sessions. On a phone a
            practice session's status badge (right) needs this room: measured at
            360 px, the title and the badge together wrapped the bar and grew the
            header by 22–43 px in every language. The badge is the one that must be
            read (ADR-112 Decision 6), so the title gives way there; desktop keeps both. */}
        {sqState?.sessionTitle && !(isPractice && isMobile) && (
          <span style={{
            fontSize: 12, fontWeight: 600, color: 'var(--accent)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            padding: '3px 10px', borderRadius: 50,
            background: 'var(--accent-soft)',
            maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {sqState.sessionTitle}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {/* Practice session (ADR-112 Decision 6): the one status badge, in the
              row that already holds the counter, so the bar's height — set by the
              Back button's tap target — and everything below it stay as they are.
              It takes the Resumed pill's place: both together overflow a phone's
              bar, and here the practice status is the one that must be read. */}
          {isPractice && <DeckStatusBadge status="practice" size="sm" />}
          {isResumed && !isPractice && (
            <span style={{
              fontSize: 11, fontWeight: 600, color: 'var(--accent)',
              textTransform: 'uppercase', letterSpacing: '0.07em',
              padding: '3px 8px', borderRadius: 50,
              background: 'var(--accent-soft)',
            }}>
              {tr.learn.resumed}
            </span>
          )}
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {done} / {queueTotal}
          </span>
        </div>
      </div>

      {/* Progress bar — permanently tight bottom margin on mobile. */}
      <div style={{ width: '100%', maxWidth: 640, marginBottom: isMobile ? 2 : 10 }}>
        <ProgressBar done={done} total={queueTotal} />
      </div>

      {/* Previous card button — desktop only. On mobile the row is collapsed
          (height 0, hidden) so the 1fr card row reliably gets the ~38px it
          needs for a three-line example at base font on a 360x640 viewport,
          AND so layout doesn't shift when the keyboard hides. Mobile users
          navigate back via a right-swipe on the card area instead (Prompt F
          — see onCardZoneTouchStart/End above). Desktop keeps the button. */}
      <div style={{
        width: '100%', maxWidth: 640,
        marginBottom: isMobile ? 0 : 18,
        height: isMobile ? 0 : undefined,
        overflow: isMobile ? 'hidden' : undefined,
      }}>
        <button
          onClick={() => openOverlayWithKeyboardDismiss(() => setShowingPrev(true))}
          disabled={!prevCard || isMobile}
          aria-hidden={!prevCard || isMobile}
          tabIndex={prevCard && !isMobile ? 0 : -1}
          style={{
            background: 'none', border: 'none', cursor: prevCard ? 'pointer' : 'default',
            color: 'var(--text-secondary)', fontSize: 12,
            padding: '4px 0', minHeight: 32,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            opacity: prevCard ? 1 : 0,
            visibility: prevCard ? 'visible' : 'hidden',
          }}
        >
          {tr.learn.previous}
        </button>
      </div>
      </div>{/* end header row */}

      {/* ── Row 2 (1fr): card zone — bounded between header and answer ─────
          Flex column anchored to the top. The card has a fixed size envelope
          (min/max height) and does not move when the keyboard opens or closes.
          Only the input field in Row 3 tracks the visual viewport. See ADR-012
          — vertical centering used to cause the card to slide when the keyboard
          hid. Anchoring at flex-start is the fix; do not reintroduce centering. */}
      <div
        onTouchStart={onCardZoneTouchStart}
        onTouchEnd={onCardZoneTouchEnd}
        onTouchCancel={onCardZoneTouchCancel}
        style={{
          width: '100%', maxWidth: 640,
          minHeight: 0, overflow: 'hidden',
          padding: isMobile ? '2px 16px' : '0 16px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >

      {effectiveFormat === 'listening' ? (
        <ListeningExercise
          term={termSide?.content ?? ''}
          definition={definitionSide?.content ?? currentCard.prompt.content}
          example={exampleSide?.content}
          phase={
            resultState === 'none' ? 'idle'
            : resultState === 'correct' ? 'correct'
            : resultState === 'wrong' ? 'wrong'
            : resultState === 'retype' ? 'retype'
            : 'idle'
          }
          onSubmit={handleListeningSubmit}
          onRetypeSubmit={handleListeningRetype}
          onContinue={handleListeningContinue}
          speak={speak}
          isSpeaking={isSpeaking}
          disabled={submitIsPending || retypeIsPending}
        />
      ) : effectiveFormat === 'sentence_build' ? (
        /* ── Sentence-build format ──────────────────────────────────────────── */
        <SentenceBuildExercise
          key={currentCard.card_id}
          definition={definitionSide?.content ?? currentCard.prompt.content}
          words={currentCard.sentence_words ?? []}
          correctSentence={currentCard.sentence_correct ?? ''}
          term={termSide?.content ?? ''}
          phase={
            resultState === 'correct' ? 'correct'
            : resultState === 'wrong' ? 'wrong'
            : 'idle'
          }
          onSubmit={handleSBSubmit}
          disabled={submitIsPending}
          masteryLevel={displayMastery}
          lastResult={lastResult}
        />
      ) : (
      <>
      {/* Prompt card — fixed size envelope on mobile (see ADR-012). The card
          sits anchored at the top of the card zone, never collapses below
          --learn-card-min-h, never grows past --learn-card-max-h. Internal
          flex column lets the prompt zone (FiB / Definition / Example) take
          remaining height and, only at the font-ladder floor, scroll
          internally. overflow:hidden here is purely a visual-bleed clip for
          rounded corners and bracket decorations — NOT a fitting tool. The
          Definition reveal button is rendered as a sibling below this card. */}
      <div ref={setLearnCardEl} data-testid="learn-card" style={{
        width: '100%', maxWidth: 640,
        background: 'var(--bg-surface)', borderRadius: 16,
        border: '1px solid var(--border)', boxShadow: 'var(--shadow-flashcard)',
        // MC mode on mobile: tighter top padding + smaller bottom margin so the
        // card sits noticeably higher and leaves clearance below for the
        // Definition trigger + "Choose the correct term" label + four answer
        // option buttons (ADR-013). Card keeps its size envelope — only the
        // top offset shrinks, the font ladder is unchanged.
        padding: isMobile ? '14px 14px 10px' : '28px 20px 20px',
        marginBottom: isMobile ? LEARN_CARD_BUTTON_GAP : 20,
        position: 'relative',
        ...(isMobile ? {
          // ADR-013 §4: card fills the full height of the 1fr grid row.
          // flex: 1 1 0  →  grow fills the row; basis 0 means derived
          // geometry, not content height.  A short prompt (high mastery,
          // D6) produces a tall card with empty space at the bottom — that
          // emptiness lives INSIDE the card, not below it.  How to make
          // that internal space read as earned progress is the D6 open
          // design question (ux_scenario.md §7); do not address it here.
          // maxHeight 100% is row-relative (not a px constant) and is
          // redundant under grow:1 but harmless.  overflow:hidden is a
          // visual-bleed clip for rounded corners — NOT a fitting tool.
          // flex-shrink: 1 + minHeight 0 lets the card compress when the
          // 1fr row itself shrinks (keyboard up on 360×640 viewports).
          flex: '1 1 0',
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        } : {}),
      }}>
        <BracketCorners />

        {/* Mastery dot (mobile) is rendered inline in the type-label row below,
            co-planar with "Fill in the blank" / "From memory". */}

        {promptMode === 'fib' ? (
          <>
            {/* Fill-in-the-blank mode: example sentence as primary prompt */}
            {/* Service line: type label centred + mastery pip strip on the right.
                3-column flex: left spacer (63 px) mirrors the pip slot so the
                label stays truly centred; pip right edge sits 2 px clear of the
                bracket arm (bracket inner edge = card-right − 14 px; pip right
                = − 16 px). Wave 4 §5 + ADR-014 amendment 2026-07-07. */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 10 : 20 }}>
              <div style={{ width: 63, flexShrink: 0 }} aria-hidden="true" />
              <div style={{
                flex: 1,
                fontSize: 11, fontWeight: 600,
                color: effectiveFormat === 'fill_blank_supported' ? 'var(--success)' : 'var(--accent)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                textAlign: 'center',
              }}>
                {tr.learn.fillInBlank}
              </div>
              <div style={{ marginRight: 2, flexShrink: 0 }}>
                <MasteryBadge level={displayMastery} size="dot" />
              </div>
            </div>

            <div
              ref={fibContentRef}
              style={{
                marginBottom: isMobile ? 8 : 16,
                // Mobile: prompt zone grows to fill whatever height the
                // card (flex:1 1 0, ADR-013 §4) leaves after its chrome.
                // flex-shrink:1 + minHeight:0 let the zone compress when
                // the 1fr row shrinks (keyboard up), triggering the font
                // ladder (ADR-012) and, at the floor, internal scroll.
                ...(isMobile ? {
                  flex: '1 1 auto', minHeight: 0,
                  overflowY: fibScrolls ? 'auto' : 'hidden',
                  overscrollBehavior: 'contain',
                  // D6 (DESIGN_SYSTEM "Wave 3" §4): center sparse content within
                  // the card's flex-allocated height so a short high-mastery
                  // prompt reads as composed, not top-stacked with a void below.
                  // Centered only while content FITS; at the ladder floor we
                  // switch to flex-start so scroll reads from the top (avoids the
                  // flex-center scroll-clip). Card/input geometry is unchanged
                  // (ADR-012/013) — this centers content INSIDE the card, never
                  // the card within the 1fr row.
                  display: 'flex', flexDirection: 'column',
                  justifyContent: fibScrolls ? 'flex-start' : 'center',
                  // H2 affordance (ADR-027): padding + fade mask so glyphs
                  // are never sliced at the scroll-zone boundary.
                  // The fade is paint; the padding is layout. Only the LADDER's
                  // own floor may add the padding: it settles first and nothing
                  // measures after it. Overflow found by measurement (a reveal,
                  // an expanded caption) takes the fade alone — padding there
                  // would inflate the very scrollHeight the ladder reads, and a
                  // prompt that had merely been re-measured would come back a
                  // step smaller than it started. (Observed: 24px → 20px after
                  // one open/close of the reveal.)
                  ...(fibLadder.shouldScroll ? { paddingBlock: '8px' } : {}),
                  ...(fibScrolls ? scrollFadeMask(fibOverflowing) : {}),
                } : {}),
              }}
            >
              {/* ADR-100 D7 (re-specified 2026-09-21): RU replaces the English
                  sentence with its translation in the same place, whole, with
                  the word in place and no blank of its own. It is measured by
                  the same ladder (the key includes the reveal), at the same
                  line height, so it takes the sentence's role and space. */}
              <div data-testid="fib-sentence" style={{ flexShrink: 0 }}>
                {swapOn && exampleTranslation ? (
                  <p
                    id="definition-translation-text"
                    data-testid="example-translation"
                    lang={readerLanguage ?? undefined}
                    onPointerDown={(e) => e.preventDefault()}
                    style={{
                      margin: 0,
                      fontSize: isMobile ? fibLadder.fontSize : 26,
                      fontFamily: 'var(--font-content)',
                      color: 'var(--text-primary)',
                      textAlign: 'center',
                      lineHeight: 1.7,
                    }}
                  >
                    {exampleTranslation}
                  </p>
                ) : (
                  <FillInBlankSentence
                    segments={fibData!}
                    reveal={blankReveal}
                    revealColor={blankRevealColor}
                    hintLetter={blankHintLetter}
                    speak={speak}
                    isSpeechSupported={isSpeechSupported}
                    fontSize={isMobile ? fibLadder.fontSize : 26}
                    // Form hint is rendered in the shared pinned zone below (P-L3)
                    // so it never scrolls out of view on long sentences. The inline
                    // slot in FillInBlankSentence is suppressed here.
                    inlineFormHint={false}
                  />
                )}
              </div>
            </div>

            {/* Definition shown as a quiet supporting caption below the sentence.
                On mobile it is clamped to two lines with a "More" affordance for
                the rare longer tail, so the example sentence keeps the vertical
                room. Always visible per the mastery ADR — never hidden below two
                lines pre-expansion. */}
            {definitionContent && showDefinition && (
              // display:contents — no box of its own, so the caption's
              // geometry is exactly what it was. Under RU it shows the
              // Definition's translation in the same place, same clamp.
              <div data-testid="definition-caption-wrap" style={{ display: 'contents' }}>
                <DefinitionCaption
                  text={swapOn && definitionTranslation ? definitionTranslation : definitionContent}
                  translatedLanguage={swapOn && definitionTranslation ? readerLanguage : null}
                  fit={isMobile ? CAPTION_FIT_STEPS[captionFitStep] : undefined}
                  isMobile={isMobile}
                  speak={speak}
                  isSpeechSupported={isSpeechSupported}
                  resetKey={revealKey}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Definition-only mode: no example side, show prompt (Definition) as primary */}
            {/* Label is neutral for both definition_only and definition_strict.
                The "strict / high-mastery" state is no longer signalled by a RED
                label (red is error-only, DESIGN_SYSTEM "Wave 3" §3) — the earned-
                progress cue is the magnitude-toned mastery dot (§4 / D6). */}
            {/* Service line: same 3-column layout as the fib-mode row above.
                Pip renders on both mobile and desktop (Wave 4 §5, 2026-07-07). */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 8 : 16 }}>
              <div style={{ width: 63, flexShrink: 0 }} aria-hidden="true" />
              <div style={{
                flex: 1,
                fontSize: 11, fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                textAlign: 'center',
              }}>
                {learnPromptLabel(effectiveFormat, currentCard.prompt, tr)}
              </div>
              <div style={{ marginRight: 2, flexShrink: 0 }}>
                <MasteryBadge level={displayMastery} size="dot" />
              </div>
            </div>

            <div
              ref={definitionContentRef}
              data-testid="definition-prompt-box"
              style={{
                fontSize: isMobile ? definitionLadder.fontSize : 30,
                fontFamily: 'var(--font-content)', fontWeight: 400,
                color: 'var(--text-primary)', textAlign: 'center', lineHeight: 1.35,
                marginBottom: isMobile ? (tabSides.length > 0 ? 12 : 4) : (tabSides.length > 0 ? 24 : 8),
                // Mobile: definition prompt is content-sized at base font.
                // The ladder + internal scroll only activate when the card
                // itself is constrained (keyboard up on small viewport) and
                // the definition is genuinely long. See ADR-012/015.
                ...(isMobile ? {
                  flex: '1 1 auto', minHeight: 0,
                  overflowY: definitionScrolls ? 'auto' : 'hidden',
                  overscrollBehavior: 'contain',
                  // D6 (DESIGN_SYSTEM "Wave 3" §4): this is the canonical FROM
                  // MEMORY empty-zone case — at high mastery only the short
                  // definition remains. Centering it within the flex-allocated
                  // height makes the spare card read as earned progress rather
                  // than a broken screen. Top-anchor at the scroll floor.
                  display: 'flex', flexDirection: 'column',
                  justifyContent: definitionScrolls ? 'flex-start' : 'center',
                  // Padding only at the ladder's own floor — see the note on
                  // the fill-in-the-blank zone above.
                  ...(definitionLadder.shouldScroll ? { paddingBlock: '8px' } : {}),
                  ...(definitionScrolls ? scrollFadeMask(definitionOverflowing) : {}),
                } : {}),
              }}
            >
              {/* Single block child so the flex column (D6 centering) holds ONE
                  flowing item — without this wrapper, ClickableText's per-word
                  <span>s each become a separate flex item and stack one word per
                  line (see learn-definition-one-word-per-line-diagnosis.md).
                  Mirrors the FiB branch, which already wraps
                  their text in a single block. Font/align inherit from the
                  parent; margin:0 avoids default <p> block margins. */}
              {/* onPointerDown.preventDefault() — same ADR-013 keyboard guard as
                  FillInBlankSentence and DefinitionCaption: prevents the browser
                  from blurring the answer input when the definition text is tapped. */}
              <p style={{ margin: 0 }} onPointerDown={(e) => e.preventDefault()}>
                {swapOn && definitionTranslation
                  ? (
                    <span
                      id="definition-translation-text"
                      data-testid="definition-translation-text"
                      lang={readerLanguage ?? undefined}
                    >
                      {definitionTranslation}
                    </span>
                  )
                  : <ClickableText text={currentCard.prompt.content} speak={speak} isSpeechSupported={isSpeechSupported} />}
              </p>
            </div>

            {/* No in-card answer reveal on mobile (ADR-014) — feedback lives
                entirely in the input (green/red overlay) and the mastery dot
                animation. Desktop keeps a small inline reveal below the prompt
                so feedback is visible without dropping focus to the input. */}
            {!isMobile && (
              <div style={{
                marginBottom: 8,
                minHeight: 64,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                // Soft-reject hides the answer reveal \u2014 no correct_answer to show here;
                // feedback lives in the zone below (ADR-048 \u00A71, content/flag change only).
                opacity: (resultState === 'none' || resultState === 'soft_reject') ? 0 : 1,
                visibility: (resultState === 'none' || resultState === 'soft_reject') ? 'hidden' : 'visible',
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: resultState === 'correct' ? 'var(--success)' : 'var(--danger)',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>
                  {tr.learn.answerLabel}
                </span>
                <span style={{
                  fontSize: 26,
                  fontFamily: 'var(--font-content)', fontWeight: 600,
                  color: resultState === 'correct' ? 'var(--success)' : 'var(--danger)',
                  wordBreak: 'break-word', textAlign: 'center',
                }}>
                  {lastResult?.correct_answer ?? '\u00A0'}
                </span>
              </div>
            )}
          </>
        )}

        {/* Pinned zone (P-L3): form hint + translation, with in-flow reference trigger.
            Three-column flex: a 44 px left spacer mirrors the right trigger column so
            form-hint / translation remain visually centred across the full card width.
            The trigger column is always 44 px wide — reserved even when the trigger is
            invisible — so reveal causes zero layout shift (ADR-014). flexShrink:0 keeps
            the zone pinned while the prompt region above absorbs available height
            (ADR-013 / ADR-028). WordReferenceTrigger is in-flow here (not
            position:absolute on the card) so no content can be occluded at any depth. */}
        <div data-testid="learn-pinned-zone" style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end' }}>
          {/* Left column — 44 px, balancing the right trigger column so form-hint
              and translation text centre relative to the full card width. It
              holds the Definition-translation control (ADR-100 D7), which is
              always mounted and shown by opacity alone, so the column is the
              same box whether or not the card has a translation. */}
          <div
            data-testid="pinned-left-column"
            style={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', paddingBottom: 2 }}
          >
            <DefinitionTranslationToggle
              available={definitionTranslationAvailable}
              open={swapOn}
              language={readerLanguage ?? ''}
              controlsId="definition-translation-text"
              onToggle={toggleDefinitionTranslation}
            />
          </div>
          {/* Center column: form hint + translation */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Form hint slot — reserved height, opacity-only collapse.
                Cards without a hint have identical pinned-region height to
                cards that do (ADR-021 §4/§6). */}
            {/* Under the reveal, when the translated text does not fit with
                them present, this line and the term translation below step
                aside (display:none — the height is the point) and return when
                RU is pressed again (ADR-100 D7, re-specified 2026-09-21). */}
            <div
              data-testid="form-hint-slot"
              aria-hidden={!formHint}
              style={{
                ...(hideSupportLines ? { display: 'none' } : {}),
                minHeight: 18,
                marginTop: isMobile ? 5 : 6,
                textAlign: 'center',
                fontSize: 12,
                fontStyle: 'italic',
                color: 'var(--text-secondary)',
                opacity: (formHint && showForm) ? 0.85 : 0,
                letterSpacing: '0.02em',
                lineHeight: 1.4,
              }}
            >
              {formHint ? tr.learn.formLabel(formHint) : ' '}
            </div>
            {/* Translation — always in flow, invisible when not applicable. */}
            <div data-testid="term-translation-slot" style={{
              marginTop: isMobile ? 4 : 8,
              display: hideSupportLines ? 'none' : 'flex', justifyContent: 'center',
              minHeight: 24,
              opacity: showTranslation ? 1 : 0,
              visibility: showTranslation ? 'visible' : 'hidden',
            }}>
              {/* textAlign: a translation that wraps fills the slot's width, and
                  justifyContent only centres the box — its lines then align to
                  the start. Centring the lines centres it like a one-line one. */}
              <span style={{
                fontSize: 14, color: 'var(--text-secondary)',
                fontFamily: 'var(--font-content)', fontStyle: 'italic',
                lineHeight: 1.4, textAlign: 'center',
              }}>
                {currentCard.translation
                  ? <ClickableText text={currentCard.translation} speak={speak} isSpeechSupported={isSpeechSupported} />
                  : ' '}
              </span>
            </div>
          </div>
          {/* Trigger column — always 44 px wide, flush to the bottom-right of the
              pinned zone. Space is permanently reserved; reveal is opacity/visibility
              only, so the card geometry (ADR-013) never shifts. */}
          <div style={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', paddingBottom: 2 }}>
            <WordReferenceTrigger
              visible={resultState !== 'none' && resultState !== 'soft_reject'}
              onClick={() => openOverlayWithKeyboardDismiss(() => setReferenceOpen(true))}
            />
          </div>
        </div>
        {/* Supplementary sides: tabs + content (Term, Example, Translation, Image excluded — see tabSides) */}
        {tabSides.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <SideTabs sides={tabSides} active={activeTab} onSelect={setActiveTab} />
            {activeSide && (
              <div style={{
                margin: '12px 0 0', textAlign: 'center',
                fontSize: 16, fontFamily: 'var(--font-content)',
                color: 'var(--text-secondary)', lineHeight: 1.5, minHeight: 28,
                // Tab content (supplementary sides) is the hint/translation block —
                // the only zone that may scroll internally on mobile.
                maxHeight: isMobile ? '25dvh' : undefined,
                overflowY: isMobile ? 'auto' : undefined,
                overscrollBehavior: isMobile ? 'contain' : undefined,
              }}>
                {(new Set(['Image', 'Image URL'])).has(activeSide.label) || activeSide.content.startsWith('data:image/') ? (
                  <img
                    src={activeSide.content}
                    alt=""
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                ) : <ClickableText text={activeSide.content} speak={speak} isSpeechSupported={isSpeechSupported} />}
              </div>
            )}
          </div>
        )}

      </div>


      {/* Answer section — mastery row + MC options or feedback zone (text-input field is pinned below) */}
      <div style={{ width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: isMobile ? 6 : 10, paddingBottom: isMobile ? 0 : 8 }}>

        {/* Mastery + change indicator row — desktop only. On mobile this content
            moves into the top FeedbackToast so it no longer occupies vertical
            layout space below the card (ADR-013). The toast itself is rendered
            once at the page root, not inside this loop. */}
        {!isMobile && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 24 }}>
          {/* Pip strip moved to the service line inside the card (Wave 4 §5,
              2026-07-07). This row now carries only the mastery-change delta
              (in-the-moment feedback, ADR-014 §4) on the right. */}
          <span style={{
            fontSize: 13, fontWeight: 600, color: 'var(--success)',
            opacity: resultState === 'correct' && lastResult?.is_correct && lastResult.mastery_change !== 0 ? 1 : 0,
            visibility: resultState === 'correct' && lastResult?.is_correct && lastResult.mastery_change !== 0 ? 'visible' : 'hidden',
          }}>
            {lastResult && lastResult.mastery_change !== 0
              ? tr.learn.masteryChange(lastResult.mastery_change ?? 0, !!lastResult.is_mastered)
              : ' '}
          </span>
        </div>
        )}

        {/* ── Multiple-choice format ─────────────────────────────────────────── */}
        {isMCFormat ? (
          /* Feedback zone stays in-flow above the card-zone bottom on desktop.
             Mobile uses the top FeedbackToast (ADR-013), so the inline zone
             collapses to zero height. The four answer options are pinned in
             the bottom region regardless of viewport size. */
          !isMobile && (
          <div style={{ position: 'relative', minHeight: 24, flexShrink: 0 }}>
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: resultState === 'correct' ? 1 : 0,
              visibility: resultState === 'correct' ? 'visible' : 'hidden',
            }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)' }}>{tr.learn.correctFeedback}</span>
            </div>
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: resultState === 'wrong' ? 1 : 0,
              visibility: resultState === 'wrong' ? 'visible' : 'hidden',
            }}>
              <span style={{ fontSize: 14, color: 'var(--danger)' }}>
                {lastResult && !lastResult.is_correct
                  ? tr.learn.correctMC(lastResult.correct_answer ?? '')
                  : tr.learn.incorrect}
              </span>
            </div>
          </div>
          )
        ) : (
          <>
            {/* ── Text-input format: feedback only — input + buttons live in pinned section below */}

            {/* Mobile: inline status label is replaced by the top FeedbackToast
                (ADR-013). The card itself still shows the correct/incorrect
                term in-place via the FillInBlankSentence reveal; the toast
                carries the mastery delta and the verdict. Removing the inline
                row reclaims ~22px of vertical space below the card so the
                input + keyboard fit comfortably. */}

            {/* Desktop feedback zone — full height block with prominent correct-answer reveal. */}
            {!isMobile && (
            <div style={{ minHeight: 76, position: 'relative', flexShrink: 0 }}>
              {/* Wrong / retype block — always rendered, defines height via its own content.
                  "Type it to continue" uses visibility:hidden (not display:none) when in
                  'wrong' state so it still occupies its line height, keeping the zone stable
                  between the wrong and retype transitions. */}
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                paddingTop: 8,
                opacity: (resultState === 'wrong' || resultState === 'retype') && lastResult && !lastResult.is_correct ? 1 : 0,
                visibility: (resultState === 'wrong' || resultState === 'retype') && lastResult && !lastResult.is_correct ? 'visible' : 'hidden',
              }}>
                <span style={{ fontSize: 12, color: 'var(--danger)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{tr.learn.correctAnswer}</span>
                <span style={{ fontSize: 22, fontFamily: 'var(--font-content)', color: 'var(--danger)', fontWeight: 500 }}>
                  {lastResult?.correct_answer ?? '\u00A0'}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', visibility: resultState === 'retype' ? 'visible' : 'hidden' }}>
                  {tr.learn.typeToContinue}
                </span>
              </div>

              {/* Correct feedback — overlaid absolutely so it shares the same zone
                  without contributing additional height to the flow. */}
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: resultState === 'correct' ? 1 : 0,
                visibility: resultState === 'correct' ? 'visible' : 'hidden',
              }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)' }}>{tr.learn.correctFeedback}</span>
              </div>

              {/* Soft-reject (ADR-048) no longer reserves space in this zone —
                  presentation amendment §7 (2026-07-15): guidance moved into the
                  input placeholder so the card sits flush against the input,
                  identical to the pre-soft-reject layout. */}
            </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      </div>{/* end row 2 (card zone) */}

      {/* Pinned MC options — anchored to the same bottom zone as the text input
          so the four options stay at a fixed screen position regardless of how
          tall the prompt card grows. */}
      {isMCFormat && (
        <div style={{
          flexShrink: 0, width: '100%', maxWidth: 640,
          padding: isMobile ? '6px 16px 10px' : '8px 16px 12px',
          paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
          background: 'var(--bg-page)',
        }}>
          <MultipleChoiceExercise
            options={mcOptions}
            correctAnswer={correctTerm}
            phase={
              resultState === 'correct' ? 'correct'
              : resultState === 'wrong' ? 'wrong'
              : 'idle'
            }
            selectedOption={mcSelectedOption}
            onSelect={handleMCSelect}
            disabled={submitIsPending}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Pinned input section — anchored at the bottom of the visual viewport, above
          the soft keyboard. Only renders for text-entry formats (not MC/listening/sb).
          Mobile: compact single-row with ↵ icon, no submit button, no hint.
          Desktop: full layout with submit button and keyboard hint. */}
      {isTextInputFormat && (
        <div style={{
          flexShrink: 0, width: '100%', maxWidth: 640,
          padding: isMobile ? '4px 16px' : '8px 16px',
          paddingBottom: 'max(6px, env(safe-area-inset-bottom))',
          background: 'var(--bg-page)',
          display: 'flex', flexDirection: 'column', gap: isMobile ? 0 : 10,
        }}>
          {/* Soft-reject (ADR-048) previously reserved a fixed-height row here
              (ADR-048 defect fix). Presentation amendment §7 (2026-07-15):
              removed — guidance now renders in the input placeholder below, so
              the card sits flush against the input on mobile too. */}
          {/* Answer bar — shake wrapper + prototype-spec flex row on mobile (Prompt 3, 2026-07-14).
              Mobile: outer div IS the styled bar (border/bg/shadow here, not on <input>).
                Input zone is flex:1; submit button is a 56×52 flex sibling.
              Desktop: outer div is position:relative (unchanged). <input> keeps its own styling. */}
          <div style={{
            animation: isShaking ? 'learnShake 0.4s ease-out' : undefined,
            ...(isMobile ? {
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 6px 6px 16px',
              background: inputBg,
              border: `1.5px solid ${inputBorder}`,
              borderRadius: 16,
              boxShadow: 'var(--shadow-card)',
              minHeight: 64,
              transition: 'border-color 0.2s, background 0.2s',
            } : { position: 'relative' }),
          }}>
            {/* Input zone — position:relative always (absolute overlays anchor here).
                flex:1 on mobile so the submit button stays at the right edge.
                Ref used to measure the usable placeholder width for soft-reject
                tier selection (ADR-048 §7 amendment) — on mobile this div's
                own clientWidth already excludes the submit button's space,
                since the button is a flex sibling, not a child. */}
            <div
              ref={inputZoneRef}
              style={{
                position: 'relative',
                ...(isMobile ? { flex: '1 1 0', minWidth: 0 } : {}),
              }}
            >
            {/* In-input visual overlay (ADR-014, Prompt K).
                Two mutually-exclusive modes:
                  • overlayActive=true  → feedback overlay (correct/wrong/retype-empty)
                      renders the correct answer in green/red over the input.
                  • overlayActive=false → typing overlay renders the actual letters
                      the user has typed (or the placeholder if empty), with a
                      static synthetic caret (non-blinking, end-of-text only —
                      ADR-014 caret-model amendment) while focused. This exists
                      because the underlying `type="password"` input renders bullets on
                      Chromium Android regardless of `-webkit-text-security:none`
                      — so we draw the visible glyphs ourselves and keep the
                      input's text/caret fully transparent (see comment on the
                      input below). */}
            {(() => {
              if (overlayActive) {
                const isCorrect = resultState === 'correct'
                const overlayText = isCorrect
                  ? (lastResult?.correct_answer ?? userInput)
                  : (lastResult?.correct_answer ?? '')
                // Wrong-answer reveal is two-phase (ADR-014 \u00A79): red at first,
                // then a neutral ghost placeholder after the read pause. The
                // ghost must never be red. Correct is a single green reveal.
                const isGhost = !isCorrect && retypeGhostPhase === 'ghost'
                const overlayColor = isCorrect
                  ? 'var(--success)'
                  : isGhost ? 'var(--text-secondary)' : 'var(--danger)'
                return (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute', inset: 0,
                      // Mobile: bar provides left inset; button is a flex sibling outside this zone.
                      padding: isMobile ? '10px 0' : '16px 20px',
                      // 19px Source Serif 4 on mobile per prototype spec; 20px on desktop unchanged.
                      fontSize: isMobile ? 19 : 20, fontFamily: 'var(--font-content)',
                      // Ghost drops to placeholder weight/opacity; red keeps the
                      // emphatic reveal weight. The colour + opacity fade animate
                      // (reduced motion \u2192 instant switch after the same pause).
                      fontWeight: isGhost ? 400 : 600,
                      color: overlayColor,
                      opacity: isGhost ? RETYPE_GHOST_OPACITY : 1,
                      transition: (isCorrect || reduceMotionPrev)
                        ? undefined
                        : `color ${RETYPE_GHOST_FADE_MS}ms ease-in, opacity ${RETYPE_GHOST_FADE_MS}ms ease-in`,
                      pointerEvents: 'none', borderRadius: isMobile ? 0 : 14, lineHeight: 1.4, zIndex: 1,
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    {overlayText || '\u00A0'}
                  </div>
                )
              }
              // Soft-reject guidance (ADR-048 §7 amendment, 2026-07-15): the
              // "similar word" explanation lives in the placeholder; the
              // first-letter reveal moved into the card's fill-in-blank (see
              // the FillInBlankSentence call below) so it no longer competes
              // for input width. The input is cleared automatically on soft
              // reject (useLearningSession), so this branch is reached like
              // any other empty state — typing hides the placeholder,
              // clearing restores it. The tier is fit-measured, not a
              // viewport breakpoint (placeholderTierIndex effect above).
              const isSoftReject = resultState === 'soft_reject' && lastResult != null && 'is_soft_reject' in lastResult
              const placeholderText = isSoftReject
                ? tr.learn.softRejectTiers[Math.min(placeholderTierIndex, tr.learn.softRejectTiers.length - 1)]
                : (fibData ? tr.learn.typeMissingWord : tr.learn.typeAnswer)
              const isEmpty = userInput.length === 0
              // Static synthetic caret (ADR-014 caret-model amendment,
              // 2026-07-15). A thin, non-blinking bar. It mirrors the native
              // input's selectionStart at ANY index (tap-to-reposition, §2):
              // the caret is absolutely positioned at `caretOffsetPx` — the
              // measured width of the text before the caret, minus native
              // scroll — instead of being inserted into the text flow. Two
              // reasons this must be out-of-flow: (1) an in-flow caret element
              // would push the characters after it rightward, breaking the
              // pixel-for-pixel parity with the invisible native text that
              // makes tap placement land on the right character; (2) it must
              // sit BETWEEN characters mid-word, which an in-flow flex sibling
              // cannot do without splitting the text node (also a parity risk
              // at the split seam). The text itself stays a single unbroken
              // node translated by -scrollLeft so it tracks the native input's
              // horizontal scroll on overflow. The caret is shown only in this
              // typing branch (own characters visible); the correct-answer
              // reveal branch above renders no caret (§4).
              return (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', inset: 0,
                    padding: isMobile ? '10px 0' : '16px 20px',
                    fontSize: isMobile ? 19 : 20, fontFamily: 'var(--font-content)',
                    fontWeight: 400,
                    color: isSoftReject ? 'var(--accent)' : (isEmpty ? 'var(--text-secondary)' : 'var(--text-primary)'),
                    opacity: isEmpty ? 0.55 : 1,
                    // Brief, perceptible fade so the auto-clear on soft reject
                    // reads as system feedback rather than a glitch.
                    transition: 'opacity 0.15s ease-out, color 0.15s ease-out',
                    pointerEvents: 'none', borderRadius: isMobile ? 0 : 14, lineHeight: 1.4, zIndex: 1,
                    display: 'flex', alignItems: 'center',
                    whiteSpace: 'pre', overflow: 'hidden',
                  }}
                >
                  {/* Text tracks native horizontal scroll; the empty placeholder
                      never scrolls (scrollLeft is 0 with no value). */}
                  <span style={{ transform: isEmpty ? undefined : `translateX(${-inputScrollLeft}px)`, flexShrink: 0 }}>
                    {isEmpty ? placeholderText : userInput}
                  </span>
                  {isInputFocused && (
                    <span
                      style={{
                        position: 'absolute',
                        left: caretOffsetPx,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 2,
                        height: '1.1em',
                        background: 'var(--accent)',
                      }}
                    />
                  )}
                </div>
                )
              })()}

            <input
              ref={inputRef}
              // Answer input — Prompt K (supersedes Prompts I + J).
              //
              // Diagnostic finding that motivated this rewrite:
              //   Prompt J tried to keep `type="password"` (for IME-strip
              //   suppression) and un-mask the bullets via
              //   `-webkit-text-security: none` (both inline and via the
              //   injected stylesheet). Computed-style inspection on Chrome
              //   for Android showed the rule WAS reaching the element and
              //   winning the cascade — Chromium just ignores the property
              //   on real `type="password"` inputs. The property only un-
              //   masks `type="text"` fields with CSS-applied masking; the
              //   browser's native password rendering is not affected.
              //   That is why letters still rendered as bullets despite J.
              //
              // Fix: keep `type="password"` (still the only reliable IME
              // suppression — Gboard / Samsung Keyboard / SwiftKey disable
              // the predictive strip, autocorrect, capitalize, and learn-
              // from-input unconditionally on password fields). Make the
              // input itself visually empty (transparent text, transparent
              // caret) and draw the user's letters + a synthetic (static)
              // caret in the overlay div above. The real input still owns
              // focus, selection, paste, backspace, Enter, and value — so
              // the submission flow is unchanged.
              //
              // Residuals (do not re-investigate):
              //   • Native selection highlights are invisible — uncommon on
              //     an answer field, accepted.
              //   • Mid-string taps DO reposition the caret now: handleInputTap
              //     (onClick) maps the tap x through the overlay glyph metrics
              //     and writes the native selection, overriding the browser's
              //     bullet-metric placement (ADR-014 caret amendment, 2026-07-15
              //     correction). The former end-anchored residual is resolved.
              //   • Third-party password managers (1Password / LastPass /
              //     Bitwarden) and Chrome's "Save password?" prompt remain
              //     suppressed by the data-* attrs + randomized name +
              //     not being inside a <form>.
              //   • The browser's BUILT-IN password manager keys autofill off
              //     type="password" and ignores autocomplete="off" for
              //     credential fields, so on a real origin where the user has
              //     saved site credentials it would offer to FILL a saved
              //     password over this input. autoComplete="new-password"
              //     (below) is the standard signal that suppresses filling the
              //     existing credential without dropping type="password" (which
              //     is load-bearing for IME suppression — ADR-011/013/014/027).
              //     Complete suppression is impossible while the field is
              //     type="password"; see DECISIONS.md ADR-014 §8 amendment
              //     (2026-07-10) for the accepted residual. §8's second
              //     amendment (2026-07-16) trials `autocomplete="one-time-
              //     code"` in place of `new-password` below — see that note.
              type="password"
              className="learn-answer-input"
              // aria-label identifies the field purpose for screen readers.
              // role="textbox" was removed: the ARIA role override on a
              // type="password" input creates a type/role mismatch that
              // caused Chrome on Android to treat the field as a generic text
              // field for autofill, producing address/card/credential bars.
              // Without the override, Chrome correctly applies password-type
              // autofill suppression (autocomplete="new-password" below).
              aria-label={tr.learn.answerLabel}
              // inputMode="text" keeps a normal alphanumeric keyboard layout
              // (some IMEs default to a numeric layout for password fields).
              // Non-Latin keyboards (Cyrillic, Greek, etc.) work as normal —
              // inputMode only affects which on-screen layout the IME picks,
              // not which characters it accepts. CJK composition is
              // disabled by the password-field treatment; the prompt
              // acknowledges this as a low-priority edge case.
              inputMode="text"
              // lang="zxx" — "no linguistic content" — defense in depth.
              // Password fields already short-circuit prediction, but if a
              // future browser version exposes prediction on passwords, this
              // tells the IME the field is non-linguistic regardless.
              lang="zxx"
              value={userInput}
              onChange={(e) => {
                // 'wrong' is included so the user can start typing the correct
                // answer immediately while audio plays — the 'wrong'→'retype'
                // transition (afterSpeak) preserves whatever they typed.
                // 'soft_reject' is included — editing clears the hint state so
                // the next Enter fires from 'none' (ADR-048 §1 unbounded retries).
                if (resultState !== 'none' && resultState !== 'retype' && resultState !== 'wrong' && resultState !== 'soft_reject') return
                if (resultState === 'soft_reject') setResultState('none')
                setUserInput(e.target.value)
                // Mirror the post-edit caret (insert/delete mid-word moves it).
                syncCaretFromInput()
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.shiftKey) return
                e.preventDefault()
                e.stopPropagation()
                // handleSubmit / handleRetype evaluate the FULL input value —
                // caret position is irrelevant to grading (ADR-014 §2).
                if (resultState === 'none' || resultState === 'soft_reject') handleSubmit()
                else if (resultState === 'retype') handleRetype()
                else if (resultState === 'correct') handleManualAdvance()
              }}
              // Caret handling (ADR-014 caret amendment + the two 2026-07-15
              // corrections). onPointerDown only records the press position
              // (read-only, no preventDefault — feeds the range-collapse
              // target below). onClick = app-owned tap→index placement
              // (handleInputTap) — always WRITES a collapsed selection,
              // mapping the tap x through the shared glyph metrics and
              // overriding the browser's bullet-metric placement. onSelect /
              // onKeyUp go through `handleSelectionEvent`, which collapses a
              // range selection (long-press, keyboard-toolbar Select all) to
              // a caret near the last press before it can paint bullet
              // glyphs, or otherwise just mirrors the caret: onSelect covers
              // arrow keys + drag-handle moves + selection landings, onKeyUp
              // catches held/repeated arrow/shift-arrow keys. onScroll only
              // reads (keeps the caret aligned when the native input scrolls
              // a long term). The document `selectionchange` listener (effect
              // above) — wired to the same `handleSelectionEvent` — is
              // belt-and-suspenders for Android drag handles and the actual
              // catch-all for a long-press selection landing. None of these
              // call preventDefault or move focus.
              onPointerDown={handleInputPointerDown}
              onSelect={handleSelectionEvent}
              onKeyUp={handleSelectionEvent}
              onClick={handleInputTap}
              onScroll={syncCaretFromInput}
              onFocus={() => { setIsInputFocused(true); syncCaretFromInput() }}
              onBlur={handleAnswerInputBlur}
              // Native placeholder is intentionally empty: the typing overlay
              // above renders both the user's text AND the placeholder hint.
              // Anything set here would render as bullets on Chromium Android
              // (same root cause we're working around) — leave it blank.
              // No readOnly toggling: Android IMEs (Samsung Keyboard) dismiss the
              // soft keyboard when a focused input becomes readOnly. State integrity
              // during feedback is protected by the onChange and onKeyDown guards.
              // "one-time-code" (trialed 2026-07-16, replacing "new-password"
              // from the 2026-07-10 amendment) tells the browser this field
              // holds a transient code, not a durable credential — the
              // standard signal least likely to be treated as save/fill-
              // worthy by the built-in password manager. "off" is not used;
              // browsers ignore it on credential-shaped fields. Requires
              // on-device Android smoke (ADR-014 §8 Gate) to confirm it
              // actually reduces the autofill bar / save-password prompt
              // before this is treated as settled — see DECISIONS.md
              // ADR-014 §8 amendment for the accepted-residual fallback if
              // it does not.
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="done"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
              name={`learn-answer-${currentCard.card_id}`}
              aria-autocomplete="none"
              style={{
                width: '100%', boxSizing: 'border-box',
                // Mobile: bar provides padding; input zone is flush (no extra padding).
                padding: isMobile ? '10px 0' : '16px 20px',
                // 19px Source Serif 4 on mobile per prototype spec; desktop unchanged.
                fontSize: isMobile ? 19 : 20,
                fontFamily: 'var(--font-content)',
                // Input text + caret are fully transparent at all times.
                // Visible letters and the synthetic caret come from the
                // overlay div above (Prompt K). The input still owns focus,
                // selection, paste, backspace, Enter, and `value` — only its
                // rendering is hidden.
                color: 'transparent',
                caretColor: 'transparent',
                // Mobile: bar carries border/bg; input is transparent/borderless.
                // Desktop: input carries its own border/bg as before.
                background: isMobile ? 'transparent' : inputBg,
                border: isMobile ? 'none' : `2px solid ${inputBorder}`,
                borderRadius: isMobile ? 0 : 14,
                outline: 'none',
                transition: isMobile ? undefined : 'border-color 0.2s, background 0.2s',
              }}
            />

            {/* Mobile-only submit button — occupies the 44 px right-padding slot already
                reserved by the answer input, so the input position never shifts (ADR-012/013).
                Always in the DOM; accent-circle shows when actionable, transparent when not.
                Desktop users have the explicit Submit / Confirm buttons below the input;
                this control is intentionally mobile-only (Prompt 3, 2026-07-14).
                Focus-preserving: onPointerDown/onMouseDown.preventDefault() keeps the IME
                keyboard raised after a tap — same pattern as DefinitionCaption (ADR-011/013).
                Tailwind v4 preflight appearance:button may override height on Android WebView;
                WebkitAppearance/appearance:none + minWidth/minHeight counter this. */}
            </div>{/* end input zone */}

            {isMobile && (
              <button
                type="button"
                aria-label={resultState === 'retype' ? tr.learn.confirmAnswerAria : tr.learn.submitAnswerAria}
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (submitIsPending || retypeIsPending) return
                  if (resultState === 'none' || resultState === 'soft_reject') handleSubmit()
                  else if (resultState === 'retype') handleRetype()
                  else if (resultState === 'correct') handleManualAdvance()
                }}
                style={{
                  flexShrink: 0,
                  width: 56, height: 52, minWidth: 56, minHeight: 52,
                  borderRadius: 12, border: 'none', padding: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: btnReady ? 'var(--accent-fill)' : 'var(--bg-secondary)',
                  color: btnReady ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                  boxShadow: btnReady ? 'var(--shadow-accent)' : 'none',
                  cursor: btnReady ? 'pointer' : 'default',
                  transition: 'background 0.2s, box-shadow 0.2s, color 0.15s',
                  WebkitAppearance: 'none', appearance: 'none',
                }}
              >
                {(submitIsPending || retypeIsPending) ? (
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: '#fff',
                    animation: 'learnSpin 0.7s linear infinite',
                  }} />
                ) : (
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"
                    stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden={true}
                  >
                    <path d="M4 11h13" />
                    <path d="M12 6l5 5-5 5" />
                  </svg>
                )}
              </button>
            )}
          </div>{/* end answer bar */}

          {/* Desktop-only: Submit / Confirm button zone.
              Fixed-height slot with buttons stacked absolutely; slot reserves height
              even when buttons are hidden so nothing shifts. */}
          {!isMobile && (
            <div style={{ position: 'relative', minHeight: 50 }}>
              <button
                onClick={handleSubmit}
                disabled={submitIsPending || resultState !== 'none'}
                aria-hidden={resultState !== 'none'}
                tabIndex={resultState === 'none' ? 0 : -1}
                style={{
                  position: 'absolute', inset: 0,
                  padding: '14px 24px', borderRadius: 50, border: 'none',
                  background: 'var(--accent-fill)', color: 'var(--text-on-accent)',
                  fontSize: 15, fontWeight: 600, cursor: resultState === 'none' ? 'pointer' : 'default',
                  transition: 'background 0.2s, opacity 0.15s',
                  opacity: resultState === 'none' ? 1 : 0,
                  visibility: resultState === 'none' ? 'visible' : 'hidden',
                }}
              >
                {submitIsPending ? tr.learn.checking : tr.learn.submitArrow}
              </button>
              <button
                onClick={handleRetype}
                disabled={resultState !== 'retype'}
                aria-hidden={resultState !== 'retype'}
                tabIndex={resultState === 'retype' ? 0 : -1}
                style={{
                  // Confirm (retype) is an ACTION, not an error indicator — accent,
                  // not red (DESIGN_SYSTEM "Wave 3" §2 one-accent-primary, §3 red=
                  // error-only). The error itself is already shown in red in the
                  // input overlay and the correct-answer reveal above.
                  position: 'absolute', inset: 0,
                  padding: '14px 24px', borderRadius: 50, border: 'none',
                  background: 'var(--accent-fill)', color: 'var(--text-on-accent)',
                  fontSize: 15, fontWeight: 600, cursor: resultState === 'retype' ? 'pointer' : 'default',
                  transition: 'background 0.2s, opacity 0.15s',
                  opacity: resultState === 'retype' ? 1 : 0,
                  visibility: resultState === 'retype' ? 'visible' : 'hidden',
                }}
              >
                {tr.learn.confirmBtn} →
              </button>
            </div>
          )}

          {/* Desktop-only: keyboard shortcut hint */}
          {!isMobile && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: (resultState === 'none' || resultState === 'retype') ? 1 : 0,
              visibility: (resultState === 'none' || resultState === 'retype') ? 'visible' : 'hidden',
            }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {tr.learn.enterToConfirm(resultState === 'retype')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ADR-082 boundary overlay — grid rows 2–3 only (see the component's own
          doc); gated on phase==='active' && currentCard so it only ever
          appears once there is a card for it to sit above, matching what the
          decision requires for both situations. dayBoundaryClosed is set
          exclusively from the server's own signal (see useLearningSession) —
          nothing here computes or infers a day. */}
      {phase === 'active' && currentCard && dayBoundaryClosed && headerRowHeight > 0 && (
        <DayBoundaryOverlay
          reason={dayBoundaryClosed}
          onContinue={continueAfterDayBoundary}
          busy={restartingSession}
          topOffset={headerRowHeight}
        />
      )}
    </motion.div>
    </div>
  )
}
